/* eslint-disable react/prop-types */
import { useEffect, useState, useContext, Fragment, useCallback } from 'react';
import { useForm } from 'react-hook-form';
import {
  Container,
  Box,
  Text,
  Flex,
  Spacer,
  Center,
  Table,
  Thead,
  Th,
  Tbody,
  Tr,
  Td,
  HStack,
  useDisclosure,
  useBreakpointValue,
  useToast,
  TableContainer,
  Tabs, 
  TabList, 
  TabPanels, 
  Tab, 
  TabPanel,
  Tooltip,
  Grid,
  IconButton,
  Badge,
  Button,
} from "@chakra-ui/react";

// globals
import { AppContext } from '../AppContext.jsx';

// Icons
import { AvailableIcons } from '../Icons/AvailableIcons.jsx';

// graphQL queries
import { PendingAttributionNotice } from '../graphqlCompnents/Statement/PendingAttributionNotice.jsx';
import { fetchUploadedReceiptsByStatus } from '../graphqlCompnents/Statement/fetchUploadedReceiptsByStatus.jsx';
import { fetchTransactionsByStatus } from '../graphqlCompnents/Statement/fetchTransactionsByStatus.jsx';
import { fetchUploadedReceiptsByTeamMemberByStatus } from '../graphqlCompnents/Statement/fetchUploadedReceiptsByTeamMemberByStatus.jsx';
import { searchDjtsByDivision } from '../graphqlCompnents/DJT/searchDjtsByDivision.jsx';
import { getStatusIdByStatusCode } from '../graphqlCompnents/DJT/getStatusIdByStatusCode.jsx';
import { fetchAllDivisions } from '../graphqlCompnents/Billing/fetchAllDivisions.jsx';
import { fetchQuickBooksExpenseCategories } from '../graphqlCompnents/Statement/fetchQuickBooksExpenseCategories.jsx';
import { fetchAllQuickBooksVendorNamesIds } from '../graphqlCompnents/Statement/fetchAllQuickBooksVendorNamesIds.jsx';

import { fetchAllChartOfAccounts } from '../graphqlCompnents/Statement/fetchAllChartOfAccounts.jsx';
import { addOrUpdateChartOfAccounts } from '../graphqlCompnents/Statement/addOrUpdateChartOfAccounts.jsx';
import { addCreditCardTeamMemberMap } from '../graphqlCompnents/Statement/addCreditCardTeamMemberMap.jsx';
import { updateCreditCardTeamMember } from '../graphqlCompnents/Statement/updateCreditCardTeamMember.jsx';
import { fetchAllCreditCardTeamMemberMaps } from '../graphqlCompnents/Statement/fetchAllCreditCardTeamMemberMaps.jsx';
import { fetchAllTeamMembers } from '../graphqlCompnents/Statement/fetchAllTeamMembers.jsx';
import { fetchAccountsByTeamMember } from '../graphqlCompnents/Statement/fetchAccountsByTeamMember.jsx';
import { updateCreditCardTransactionReceipt } from '../graphqlCompnents/Statement/updateCreditCardTransactionReceipt.jsx';
import { updateTransaction } from '../graphqlCompnents/Statement/updateTransaction.jsx';
import { claimTransactionForReceipt, describeClaimFailure } from '../graphqlCompnents/Statement/claimTransactionForReceipt.jsx';
import { updateInstantNotification } from '../graphqlCompnents/Statement/updateInstantNotification.jsx';
import { fetchAllUnits } from '../graphqlCompnents/Unit/fetchAllUnits.jsx';
import { updateIFTAFuelTracking } from '../graphqlCompnents/Statement/updateIFTAFuelTracking.jsx';
import { upsertQBVendorLocal } from '../graphqlCompnents/Statement/upsertQBVendorLocal.jsx';
import { addIFTAFuelTracking } from '../graphqlCompnents/Statement/addIFTAFuelTracking.jsx';

import { ReconnectCapitalOneCard } from './ReconnectCapitalOneCard.jsx';

// QBO proxy-based helpers
import {
  getCustomersViaProxy,
  getClassesViaProxy,
  // getExpenseCategoriesViaProxy,
  getRecentPurchasesViaProxy,
  getRecentBillsViaProxy,
  getChartOfAccountsViaProxy,
  createVendorViaProxy,
  getVendorViaProxy,
} from "../QuickBooks/qboClient.jsx";

// Still using legacy vendor sync / vendor creation for now
// pushStatementToQuickBooks remains for now, since it’s your custom op
import { pushStatementToQuickBooks } from "../QuickBooks/pushStatementToQuickBooks.jsx";

// graphql
import { addQuickBooksVendor } from '../graphqlCompnents/Statement/addQuickBooksVendor.jsx';
// structural components
import { ModuleBox } from '../Structural/ModuleBox.jsx';
import { ModuleDrawer } from '../Structural/ModuleDrawer.jsx';

// drawer components
import { EditTransaction } from './EditTransaction.jsx';
import { EditTransactionVendor } from './EditTransactionVendor.jsx';
import { QuickCameraUploadStatement } from '../ReceiptUpload/QuickCameraUploadStatement.jsx';

// external components
// form components
import { TextInput } from '../Form/Input/TextInput.jsx';
import { FormSelectSimple } from '../Form/Select/FormSelectSimple.jsx';
import { ButtonSecondaryWithIcon } from '../Form/Button/ButtonSecondaryWithIcon.jsx';

// external components
import { StatementUploader } from './StatementUploader.jsx';
// Buttons
import { ButtonQuaternaryWithIcon } from '../Form/Button/ButtonQuaternaryWithIcon.jsx';
import { ButtonPrimaryPlain } from '../Form/Button/ButtonPrimaryPlain.jsx';
import { ButtonSecondaryPlain } from '../Form/Button/ButtonSecondaryPlain.jsx';
import { ResponsiveCancelCloseButton } from '../Form/Button/ResponsiveCancelCloseButton.jsx';

// external functions
import { normalizeMerchantName, canonicalizeVendorName, matchQbVendorByName, findMatchesWithQbVendors, findNotificationDirectMatches } from '../functions/transactionMatching.jsx';
import { formatCurrency } from '../functions/currency.jsx';
import { spellOutDate, awsFormatUnknownDate } from '../functions/dateTime.jsx';
import { truncateString } from '../functions/strings.jsx';
import { toTitleCase } from '../functions/strings.jsx';

// Modals
import { ReceiptImageViewerModal } from '../Modal/ReceiptImageViewerModal.jsx';

import { getSignedUrlForFile } from '../custom-hooks/useSignedUrlForFile.jsx';

import { useQboConnectionMeta } from "../QuickBooks/useQboConnectionMeta.jsx";

// A transaction counts as "has a receipt" only when the link field holds a real id.
// Detach writes '' (and older paths may leave null, undefined, or '0'), so a strict
// === null check hides detached transactions from the cardholder view.
const hasLinkedReceipt = (txn) => {
  const id = txn?.capitalOneCreditCardTransactionReceiptId;
  return !!id && id !== '0';
};

// Receipt statuses that count as "uploaded but not yet matched" for a cardholder.
// Must stay in sync with the statuses fetched in handleSelectCardholder.
const UNMATCHED_RECEIPT_STATUSES = [
  'preemptive',
  'preemptive-invoice',
  'preemptive-refund',
  'preemptive-admin',
  'preemptive-invoice-admin',
  'preemptive-refund-admin',
  'with-notification',
  'with-notification-admin',
];

const mapQbErrorToFriendlyBase = (qbResult) => {
  if (!qbResult) {
    return {
      status: "error",
      title: "QuickBooks publish failed",
      description: "Unknown error occurred while publishing to QuickBooks.",
    };
  }

  const { stage, errorType, message, detail, attachments } = qbResult;

  // Base message pieces
  const baseMessage = message || "QuickBooks publish failed.";
  const baseDetail = detail || "";

  // Attachment-related partials
  if (qbResult.partial && stage === "attachFilesPartial") {
    const failedCount = attachments?.failed || 0;
    const total = attachments?.total || 0;

    let desc =
      `QuickBooks created the transaction, but ${failedCount} of ${total} attachment(s) failed.`;
    if (baseDetail) {
      desc += ` ${baseDetail}`;
    }

    return {
      status: "warning",
      title: "QuickBooks partial success",
      description: desc,
    };
  }

  // Stage-specific mapping
  if (stage === "auth") {
    return {
      status: "error",
      title: "QuickBooks authentication error",
      description:
        baseDetail ||
        baseMessage ||
        "Unable to authenticate with QuickBooks. Please check the shared connection.",
    };
  }

  if (stage === "createTxn" || errorType === "CreateTxnFailed") {
    // Check for deleted vendor error
    if (baseDetail?.includes("has been deleted") || baseDetail?.includes("you must restore")) {
      // Extract vendor name from the error message
      const vendorMatch = baseDetail.match(/restore (.+?)(?:\s*\(deleted\)|\.)/);
      const vendorName = vendorMatch ? vendorMatch[1] : "the vendor";
      
      return {
        status: "error",
        title: "Deleted Vendor Error",
        description: `The vendor "${vendorName}" has been deleted in QuickBooks. Please restore it in QuickBooks or select a different vendor for this receipt.`,
      };
    }

    // Check for other invalid reference errors
    if (baseDetail?.includes("Invalid Reference Id")) {
      return {
        status: "error",
        title: "Invalid Reference Error",
        description: baseDetail || "One of the selected items (vendor, account, class, etc.) is invalid or deleted in QuickBooks.",
      };
    }

    return {
      status: "error",
      title: "Could not create transaction in QuickBooks",
      description:
        baseDetail ||
        baseMessage ||
        "QuickBooks rejected the transaction. Please verify vendor, account, and amount.",
    };
  }

  if (stage === "init" && errorType === "ValidationError") {
    return {
      status: "error",
      title: "Missing or invalid data",
      description: baseDetail || baseMessage,
    };
  }

  // Fallback
  return {
    status: qbResult.partial ? "warning" : "error",
    title: qbResult.partial
      ? "QuickBooks partial success"
      : "QuickBooks publish failed",
    description: baseDetail || baseMessage,
  };
};

const NOTHING_CREATED_NOTE = "Nothing was created in QuickBooks.";
const MAYBE_CREATED_NOTE =
  "QuickBooks may have created it anyway. Check QuickBooks before publishing this receipt again.";

const mapQbErrorToFriendly = (qbResult) => {
  const friendly = mapQbErrorToFriendlyBase(qbResult);
  if (!qbResult || friendly.status !== "error") return friendly;

  let note = null;
  if (qbResult.nothingCreated === true) {
    note = NOTHING_CREATED_NOTE;
  } else if (qbResult.nothingCreated === false && qbResult.stage === "createTxnUncertain") {
    note = MAYBE_CREATED_NOTE;
  }
  if (!note) return friendly;

  const desc = (friendly.description || "").trim();
  const joined = desc ? `${desc}${/[.!?]$/.test(desc) ? "" : "."} ${note}` : note;
  return { ...friendly, description: joined };
};

const getValidAmount = (...amounts) => {
  for (let amount of amounts) {
    if (typeof amount !== 'string' && typeof amount !== 'number') continue;
    let strAmount = String(amount).replace(/[^\d.]/g, '');
    if (/^\d{3,}$/.test(strAmount)) {
      const inferred = `${strAmount.slice(0, -2)}.${strAmount.slice(-2)}`;
      const num = parseFloat(inferred);
      if (num > 0) return num.toFixed(2);
    } else {
      const num = parseFloat(strAmount);
      if (!isNaN(num) && num > 0) return num.toFixed(2);
    }
  }
  return '';
};

export const ReceiptTransactionManagement = () => {

  // grab global context values
  const { store } = useContext(AppContext);

  const adminName = store?.userData?.name||'';
  // get the current environment sandbox || production
  const qbEnv = store?.qbEnvironment || 'sandbox';

  const { 
    meta, 
    loading, 
    error, 
    isConnected: canUseQbo,   // rename at destructure, one less variable
  } = useQboConnectionMeta(qbEnv, { enabled: Boolean(qbEnv) });

  const toast = useToast();
  const hasEnv = !!qbEnv;
  const hasMeta = !!meta;

  // For UI hints:
  const showInitBanner = hasEnv && loading && !hasMeta && !error;

  const currentUserIsGlobalAdmin = store?.userData?.isGlobalAdmin||false;
  const currentTeamMemberId = store?.userData?.id||'';
  const usersGroups = store?.userData?.groups||[];

  let userIsCreditCardManager = false;
  if (usersGroups.includes('credit_card_receipt_manager') || usersGroups.includes('credit_card_manager')) {
    userIsCreditCardManager = true;
  }

  let userIsDeveloper = false;
  if (usersGroups.includes('developer')) {
    userIsDeveloper = true;
  }

  const [ lastQbError, setLastQbError ] = useState(null);
  const [isMatchingAll, setIsMatchingAll] = useState(false);
  
  const matchAllTransactions = async () => {
    if (isMatchingAll || isMatching) return;
    if (!autoMatchedReceipts?.length) return;

    setIsMatchingAll(true);

    try {
      const total = autoMatchedReceipts.length;
      let successCount = 0;
      let errorCount = 0;

      const claimedTxnIds = new Set();

      for (let i = 0; i < total; i++) {
        const matched = autoMatchedReceipts[i];
        const txnId = matched?.matchedTransaction?.id;

        if (!txnId || claimedTxnIds.has(txnId)) {
          console.warn(`⚠️ Skipping ${matched?.id} — transaction already claimed in this run`);
          errorCount++;
          continue;
        }
        claimedTxnIds.add(txnId);
        
        console.log(`🔄 Matching ${i + 1} of ${total}:`, matched.id);

        try {
          await matchTransactionForApproval(matched, { refresh: false });
          successCount++;
        } catch (error) {
          console.error(`❌ Failed to match ${matched.id}:`, error);
          errorCount++;
        }

        // Small delay between operations (no refresh between them)
        if (i < total - 1) {
          await new Promise(resolve => setTimeout(resolve, 100));
        }
      }

      console.log(`✅ Successfully matched ${successCount} of ${total} transactions`);
      
      // Single refresh at the end to update the UI with all changes
      setRefreshIndex(prev => prev + 1);
      
      if (errorCount > 0) {
        console.warn(`❌ ${errorCount} transactions failed to match`);
      }
    } catch (error) {
      console.error("Error in bulk matching:", error);
    } finally {
      setIsMatchingAll(false);
    }
  };
  const [isPublishingToQuickBooks, setIsPublishingToQuickBooks] = useState(false);

  // --- REACT-HOOK-FORM ---
  const { control, register, handleSubmit, getValues, setValue, setError, clearErrors, trigger, formState: { errors, isSubmitting } } = useForm({
    mode: 'onSubmit',
    reValidateMode: 'onChange',
    defaultValues: {}
  });
  
  const onError = (errors) => {
    console.log('Form errors:', errors);
  };

  // QUICKBOOKS

  // ----- EDIT TRANSACTION DRAWER -----
  const { isOpen: isEditTransactionOpen , onOpen: onEditTransactionOpen, onClose: editTransactionClose } = useDisclosure()
  // ----- EDIT TRANSACTION VENDOR DRAWER -----
  const { isOpen: isEditTransactionVendorOpen , onOpen: onEditTransactionVendorOpen, onClose: editTransactionVendorClose } = useDisclosure()
  // ----- GALLERY MODAL -----
  const { isOpen: isReceiptImageViewerModalOpen, onOpen: onReceiptImageViewerModalOpen, onClose: onReceiptImageViewerModalClose } = useDisclosure();

  const [attachReceiptMode, setAttachReceiptMode] = useState('upload'); // 'upload' | 'link-existing'
  const [isLinkingReceipt, setIsLinkingReceipt] = useState(false);
  const [pendingLinkReceipt, setPendingLinkReceipt] = useState(null);
  
  const handleLinkExistingReceipt = async () => {
    if (!pendingLinkReceipt || !currentAttachTransaction) return;
    setIsLinkingReceipt(true);
    try {
      // Claim the transaction first; only if it is free (or already this
      // receipt's) is the receipt updated. Nothing is half-written on refusal.
      const claim = await claimTransactionForReceipt({
        transactionId: currentAttachTransaction.id,
        receiptId: pendingLinkReceipt.id,
      });
      if (!claim.ok) {
        toast({
          title: 'Receipt not linked',
          description: describeClaimFailure(claim),
          status: 'error',
          duration: 10000,
          isClosable: true,
        });
        return;
      }
      await updateCreditCardTransactionReceipt({
        id: pendingLinkReceipt.id,
        matchStatus: 'with-transaction-admin',
        plaidCreditCardTransactionId: currentAttachTransaction.id,
      });
      setPendingLinkReceipt(null);
      onAttachReceiptClose();
      setAttachReceiptMode('upload');
      toast({
        title: 'Receipt linked',
        description: `Receipt linked to ${currentAttachTransaction.merchantEntityName || currentAttachTransaction.store || 'transaction'} successfully.`,
        status: 'success',
        duration: 4000,
        isClosable: true,
      });
      if (selectedCardholderTeamMemberId) {
        handleSelectCardholder({
          value: selectedCardholderTeamMemberId,
          name: selectedCardholderName,
          divisionsForSearch: adminUsers.find(u => u.value === selectedCardholderTeamMemberId)?.divisionsForSearch || [],
        });
      }
    } catch (err) {
      console.error('Failed to link existing receipt:', err);
      toast({
        title: 'Link failed',
        description: 'Something went wrong linking the receipt. Please try again.',
        status: 'error',
        duration: 5000,
        isClosable: true,
      });
    } finally {
      setIsLinkingReceipt(false);
    }
  };

  const [statusFilter, setStatusFilter] = useState('all');
  const [sortField, setSortField] = useState('');
  const [sortDirection, setSortDirection] = useState('asc');

// Status options for the filter
  const statusOptions = [
    { value: 'all', label: 'All Statuses' },
    { value: 'published', label: 'Published' },
    { value: 'ready_for_approval', label: 'Ready for Approval' },
    { value: 'pending_posted', label: 'Pending Posted Transaction' },
    { value: 'pending_submission', label: 'Pending User Submission' }
  ];

  // Handler for status filter change
  const handleStatusFilterChange = (selectedOption) => {
    setStatusFilter(selectedOption?.value || 'all');
  };

  // Handler for sorting
  const handleSort = (field) => {
    if (sortField === field) {
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortDirection('asc');
    }
  };

  const [ galleryImages, setGalleryImages ] = useState([]);
  const [ galleryTransaction, setGalleryTransaction ] = useState({});
  const handleOpenGallery = (imagesArray, transaction) => {
    console.warn('openGallery images: ', imagesArray);
    console.warn('openGallery transaction: ', transaction);
    setGalleryImages(imagesArray);  // Set gallery images first
    setGalleryTransaction(transaction);  // Set gallery transaction data
  };
  
  // useEffect to watch galleryImages and open modal only when it's set
  useEffect(() => {
    if (galleryImages.length > 0) {
      console.log('Gallery images set, opening modal...');
      onReceiptImageViewerModalOpen();
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [galleryImages]); // Depend on galleryImages, so it triggers only when set

  const handleReceiptImageViewerModalClose = () => {
    setGalleryImages([]);  // Clear gallery images
    setGalleryTransaction({});  // Clear gallery transaction data
    onReceiptImageViewerModalClose();
  };

  const [ uploadedReceipts, setUploadedReceipts ] = useState([]);
  const [ uploadedUnmatchedReceipts, setUploadedUnmatchedReceipts ] = useState([]);
  const [ syncedTransactions, setSyncedTransactions ] = useState([]);
  const [ uploadedDocuments, setUploadedDocuments ] = useState([]);
  const [ allPendingTransactions, setAllPendingTransactions ] = useState([]);
  const [ teamMemberCardLookup, setTeamMemberCardLookup ] = useState({});
  const [ currentAttachTransaction, setCurrentAttachTransaction ] = useState(null);
  const [ cardholderUnmatchedTxns, setCardholderUnmatchedTxns ] = useState([]);

  const { isOpen: isAttachReceiptOpen, onOpen: onAttachReceiptOpen, onClose: onAttachReceiptClose } = useDisclosure();
  const { isOpen: isStandaloneUploadOpen, onOpen: onStandaloneUploadOpen, onClose: onStandaloneUploadClose } = useDisclosure();

  
  const [matchResults, setMatchResults] = useState({
    matches: [],
    unmatchedFromArray1: [],
    unmatchedFromArray2: [],
  });
  
  const [ refreshIndex, setRefreshIndex ] = useState(0);
  const handleRefresh = () => {
    setMatchResults({
      matches: [],
      unmatchedFromArray1: [],
      unmatchedFromArray2: [],
    });
    
    setRefreshIndex(prev => prev + 1); // Increment to trigger useEffect
    setValue('searchInput', '')
  };

  const [unpublishedInvoices, setUnpublishedInvoices] = useState([]);

  useEffect(() => {
    if (matchResults?.unmatchedFromArray1?.length > 0) {
      // Filter to get all items with matchStatus === 'preemptive-invoice'
      const invoices = matchResults.unmatchedFromArray1.filter(
        item => item.matchStatus === 'preemptive-invoice'
      );

      console.log(' 👽 Pending Invoices:', invoices);
      setUnpublishedInvoices(invoices); // This ensures it's always an array
    } else {
      setUnpublishedInvoices([]); // Reset if no matchResults or empty array
    }
  }, [matchResults, refreshIndex]);

  const [ units, setUnits ] = useState([]);
  useEffect(() => {
    const fetchUnits = async () => {
      try {
        const results = await fetchAllUnits();
        console.log('Fetched units:', results);
        setUnits(results);
      } catch (error) {
        console.error('Error fetching units:', error);
      }
    };

    fetchUnits();
  },[]);

  const [ expenseCategories, setExpenseCategories ] = useState([])
  const getExpenseCategory = (qbId) => {
    // Find the expense that matches the given qbId
    const expense = expenseCategories.find((category) => category.value === qbId);

    // If no expense is found, return null or a default value
    if (!expense) return '';

    // Return the formatted object based on the found expense
    return {
      value: expense.value,
      label: expense.label,
      isIFTATracked: expense?.isIFTATracked === true ? true : false,
    };
  };

  const [qbVendors, setQBVendors] = useState([]);

  useEffect(() => {
    let isMounted = true; // Track if the component is still mounted

    const fetchQuickBooksVendors = async () => {
      try {
        const vendors = await fetchAllQuickBooksVendorNamesIds({ startsWith: '', limit: 6000 });
        
        if (isMounted) {
          // Normalize the vendor names and add a `normalizedName` property
          const normalizedVendors = vendors?.items.map(vendor => ({
            ...vendor,
            normalizedName: normalizeMerchantName(vendor.qbDisplayName)
          }));

          setQBVendors(normalizedVendors || []);
        }
      } catch (err) {
        if (isMounted) {
          console.error('Failed to fetch QuickBooks vendors:', err);
        }
      }
    };

    if (qbEnv) {
      fetchQuickBooksVendors();
    }

    // Cleanup function to set isMounted to false
    return () => {
      isMounted = false;
    };
  }, [qbEnv, refreshIndex]);


  useEffect(() => {
    const fetchUploadedReceipts = async () => {
      try {
        const [readyResults, fixedIssueResults, hasIssueResults, invoices, withNotificationResults, withTransactionAdminResults, withNotificationAdminResults] = await Promise.all([
          fetchUploadedReceiptsByStatus({status: 'ready-for-approval',      limit: 750}),
          fetchUploadedReceiptsByStatus({status: 'fixed-issue',             limit: 100}),
          fetchUploadedReceiptsByStatus({status: 'has-issue',               limit: 100}),
          fetchUploadedReceiptsByStatus({status: 'preemptive-invoice',      limit: 100}),
          fetchUploadedReceiptsByStatus({status: 'with-notification',       limit: 750}),
          fetchUploadedReceiptsByStatus({status: 'with-transaction-admin',  limit: 500}),
          fetchUploadedReceiptsByStatus({status: 'with-notification-admin', limit: 500}),
        ]);

        const results = [
          ...(readyResults || []),
          ...(fixedIssueResults || []),
          ...(hasIssueResults || []),
          ...(invoices || []),
          ...(withNotificationResults || []),
          ...(withTransactionAdminResults || []),
          ...(withNotificationAdminResults || []),
        ];
        
        // Iterate through results to get signed URLs for each item
        const resultsWithSignedUrls = await Promise.all(
          results?.map(async (item) => {
            const uploadedDocuments = item?.uploadedDocuments?.items || [];
            // Fetch signed URLs for each document in parallel
            const signedUrls = await Promise.all(
              uploadedDocuments.map(async (doc) => {
                const { identityId, key, bucket } = doc?.uploadedDocument || {};
                if (identityId && key && bucket) {
                  try {
                    const url =  await getSignedUrlForFile({
                      bucketName: bucket,
                      identityId: identityId,
                      fileName: key,
                      fileSecurity: 'protected',
                    });
                    return url;
                  } catch (error) {
                    console.error(`Error fetching signed URL for document ${key}:`, error);
                    return null; // Skip if the URL fetch fails
                  }
                }
                return null; // Skip if any value is missing
              })
            );
  
            // Attach signed URLs to the current item, filtering out any null results
            return {
              ...item,
              signedUrls: signedUrls.filter(Boolean),
            };
          })
        );
        // Store processed data with signed URLs in state
        setUploadedReceipts(resultsWithSignedUrls);
      } catch (error) {
        console.error('Error fetching uploaded receipts or signed URLs:', error);
      }
    };
  
    if (currentUserIsGlobalAdmin || userIsCreditCardManager) {
      fetchUploadedReceipts();
    }
  }, [currentUserIsGlobalAdmin, userIsCreditCardManager, refreshIndex]);

  const [ qbPurchases, setQbPurchases ] = useState([]);
  const [ qbBills, setQbBills ] = useState([]);

  useEffect(() => {
    if (!(currentUserIsGlobalAdmin || userIsCreditCardManager)) return;
    if (!qbEnv || !canUseQbo) return;

    // Rolling lookback window for the Verify tab. The backend derives the actual
    // window boundaries from this hour count — we deliberately do NOT send a
    // calendar date. The previous `new Date().toISOString().split("T")[0]`
    // produced a *UTC* day, so from 7pm Central onward the UI asked QBO for
    // tomorrow and both lists came back empty.
    const VERIFY_LOOKBACK_HOURS = 72;
    console.log("Fetching QuickBooks purchases and bills created in the last", VERIFY_LOOKBACK_HOURS, "hours");

    // This effect can fire more than once per mount. Without a guard, whichever
    // response resolves LAST wins the setState — which is how an empty result
    // from a stale/early call was clobbering a good one and blanking the tab.
    let cancelled = false;

    const getPurchases = async () => {
      try {
        const purchases = await getRecentPurchasesViaProxy({
          qbenv: qbEnv,
          app: "shared",
          hours: VERIFY_LOOKBACK_HOURS,
        });

        if (cancelled) return;
        const arr = Array.isArray(purchases) ? purchases : [];
        console.info("========== getRecentPurchasesViaProxy:", arr);
        setQbPurchases(arr);
      } catch (error) {
        console.error("Failed to fetch QB purchases via proxy:", error);
        if (cancelled) return;
        setQbPurchases([]);
      }
    };

    const getBills = async () => {
      try {
        const bills = await getRecentBillsViaProxy({
          qbenv: qbEnv,
          app: "shared",
          hours: VERIFY_LOOKBACK_HOURS,
        });

        if (cancelled) return;
        const arr = Array.isArray(bills) ? bills : [];
        console.info("========== getRecentBillsViaProxy:", arr);
        setQbBills(arr);
      } catch (error) {
        console.error("Failed to fetch QB bills via proxy:", error);
        if (cancelled) return;
        setQbBills([]);
      }
    };

    if (qbEnv && canUseQbo && (currentUserIsGlobalAdmin || userIsCreditCardManager)) {
      getPurchases();
      getBills();
    }

    return () => { cancelled = true; };
  }, [
    currentUserIsGlobalAdmin,
    userIsCreditCardManager,
    refreshIndex,
    qbEnv,
    canUseQbo,
  ]);

  useEffect(() => {
    const fetchUploadedUnmatchedReceipts = async () => {
      try {
        // const results = await fetchUploadedReceiptsByStatus({ status: 'preemptive', limit: 750 });
        const [preemptive, preemptiveAdmin, preemptiveInvoiceAdmin, preemptiveRefundAdmin,
              withNotification, withNotificationAdmin] = await Promise.all([
          fetchUploadedReceiptsByStatus({ status: 'preemptive',               limit: 750 }),
          fetchUploadedReceiptsByStatus({ status: 'preemptive-admin',         limit: 500 }),
          fetchUploadedReceiptsByStatus({ status: 'preemptive-invoice-admin', limit: 500 }),
          fetchUploadedReceiptsByStatus({ status: 'preemptive-refund-admin',  limit: 500 }),
          fetchUploadedReceiptsByStatus({ status: 'with-notification',        limit: 750 }),
          fetchUploadedReceiptsByStatus({ status: 'with-notification-admin',  limit: 500 }),
        ]);

        const results = [
          ...(preemptive              || []),
          ...(preemptiveAdmin         || []),
          ...(preemptiveInvoiceAdmin  || []),
          ...(preemptiveRefundAdmin   || []),
          ...(withNotification        || []),
          ...(withNotificationAdmin   || []),
        ];
        // Iterate through results to get signed URLs for each item
        const resultsWithSignedUrls = await Promise.all(
          results?.map(async (item) => {
            const uploadedDocuments = item?.uploadedDocuments?.items || [];
            // Fetch signed URLs for each document in parallel
            const signedUrls = await Promise.all(
              uploadedDocuments.map(async (doc) => {
                const { identityId, key, bucket } = doc?.uploadedDocument || {};
                if (identityId && key && bucket) {
                  try {
                    const url =  await getSignedUrlForFile({
                      bucketName: bucket,
                      identityId: identityId,
                      fileName: key,
                      fileSecurity: 'protected',
                    });
                    return url;
                  } catch (error) {
                    console.error(`Error fetching signed URL for document ${key}:`, error);
                    return null; // Skip if the URL fetch fails
                  }
                }
                return null; // Skip if any value is missing
              })
            );
  
            // Attach signed URLs to the current item, filtering out any null results
            return {
              ...item,
              signedUrls: signedUrls.filter(Boolean),
            };
          })
        );
        // Store processed data with signed URLs in state
        setUploadedUnmatchedReceipts(resultsWithSignedUrls);
      } catch (error) {
        console.error('Error fetching uploaded receipts or signed URLs:', error);
      }
    };
  
    if (currentUserIsGlobalAdmin || userIsCreditCardManager) {
      fetchUploadedUnmatchedReceipts();
    }
  }, [currentUserIsGlobalAdmin, userIsCreditCardManager, refreshIndex]);


  const [ transactionsByCard, setTransactionsByCard ] = useState([]);
  const [ mappedTeamMembers, setMappedTeamMembers ] = useState([]);

  {/* Pending Transactions */}
  useEffect(() => {

    const transactionDataByCardNumber = (transactions, mappedMembers = []) => {
      // Create a lookup map for quick matching of card numbers to team members
      const memberMap = Array.isArray(mappedMembers)
        ? mappedMembers.reduce((acc, member) => {
            if (member.teamMember) { // Ensure teamMember is not null
              acc[member.cardLastFour] = {
                firstName: member.teamMember.firstName || '',
                lastName: member.teamMember.lastName || '',
                goesBy: member.teamMember.goesBy || '',
              };
            }
            return acc;
          }, {})
        : {};
    
      // Group transactions by their accountNumber (card number)
      const groupedTransactions = transactions.reduce((acc, transaction) => {
        const accountNumber = transaction.accountNumber;
    
        if (accountNumber) {
          if (!acc[accountNumber]) {
            acc[accountNumber] = {
              accountNumber,
              count: 0,
              transactions: [],
              teamMemberName: 'Unknown Team Member', // Default
            };
    
            // Add team member info if available
            const teamMember = memberMap[accountNumber];
            if (teamMember) {
              acc[accountNumber].teamMemberName = teamMember.goesBy
                ? `${teamMember.goesBy} ${teamMember.lastName}`
                : `${teamMember.firstName} ${teamMember.lastName}`;
            }
          }
    
          // Add transaction to the grouped list and increment the count
          acc[accountNumber].transactions.push(transaction);
          acc[accountNumber].count++;
        }
    
        return acc;
      }, {});
    
      // Convert the grouped object into an array sorted by count (descending)
      return Object.values(groupedTransactions).sort((a, b) => b.count - a.count);
    };

    const enrichTransactionsWithTeamMember = (transactions, mappedMembers = []) => {
      // Create a lookup map for quick matching
      const memberMap = Array.isArray(mappedMembers)
        ? mappedMembers.reduce((acc, member) => {
            if (member.teamMember) { // Only process non-null teamMember
              acc[member.cardLastFour] = {
                firstName: member.teamMember.firstName || '',
                lastName: member.teamMember.lastName || '',
                goesBy: member.teamMember.goesBy || ''
              };
            }
            return acc;
          }, {})
        : {};
    
      // Enrich transactions with matching team member details
      return transactions.map(transaction => {
        const teamMember = memberMap[transaction.accountNumber];
        return {
          ...transaction,
          teamMember: teamMember || null,
        };
      });
    };

    const fetchSyncedTransactions = async () => {
      try {
        const results = await fetchTransactionsByStatus('pending_match', 1000);
        if (!results) return;
        // console.info("##### fetchSyncedTransactions results: ", results);
    
        const formattedResults = results.map(tx => ({
          ...tx,
          amount: formatAmount(tx.amount), // ✅ Clean the amount here!
        }));
    
        // Enrich transactions with teamMember details
        const enrichedTransactions = enrichTransactionsWithTeamMember(
          formattedResults,
          mappedTeamMembers || []
        );
        console.log("***** Enriched Transactions: ", enrichedTransactions);
    
        setSyncedTransactions(enrichedTransactions);
    
        // Get transactions grouped by card with detailed rows
        const resultsByCard = transactionDataByCardNumber(
          enrichedTransactions,
          mappedTeamMembers || []
        );
        // console.warn("***** resultsByCard: ", resultsByCard);
    
        setTransactionsByCard(resultsByCard); // Update state with the grouped results
      } catch (error) {
        console.error("Error fetching synced transactions:", error);
      }
    };

    // Only fetch if conditions are met
    if ((currentUserIsGlobalAdmin || userIsCreditCardManager) && mappedTeamMembers?.length > 0) {
      fetchSyncedTransactions();
    }
    // console.log('mappedTeamMembers: ', mappedTeamMembers);
  }, [currentUserIsGlobalAdmin, userIsCreditCardManager, refreshIndex, mappedTeamMembers]);

  useEffect(() => {
    if (uploadedReceipts.length && syncedTransactions.length && expenseCategories.length && qbVendors.length && refreshIndex) {
      const config = {
        amountField1: 'capitalOneInstantNotification.purchaseAmount',
        amountField2: 'amount',
        accountField1: 'capitalOneInstantNotification.lastFour',
        accountField2: 'accountNumber',
        dateField1: 'capitalOneInstantNotification.purchaseDate',
        dateField2: 'date',
        merchantField1: 'capitalOneInstantNotification.purchaseMerchant',
        merchantField2: 'merchantEntityName',

        amountWeight: 15,               // Reduced to lower the impact of amount matching
        amountTolerancePercentage: 20,  // Allow for tipping up to 20% of the amount (only applied on categoryIds for restaurants)
        accountWeight: 25,              // Keep accountWeight high for strict matching
        dateWeight: 10,                 // Slightly lower to emphasize merchant over date
        dateProximity: 3,               // Date proximity as 3 days for vendor to batch transactions
        merchantWeight: 50,             // Significantly increased to prioritize merchant similarity

        minimumScoreThreshold: 5,      // Keep minimum threshold low to allow for more matches

        // Separate similarity configurations for merchant and vendor matching
        minimumMerchantSimilarity: 0.8,  // Keep this high for merchantEntityName matching
        minimumQbVendorSimilarity: 0.6,  // New value for qbVendor matching to loosen it up

        // Array of category IDs representing Plaid categories for 
        // [ { "S" : "Food and Drink" }, { "S" : "Restaurants" } ] 13005000
        // [ { "S" : "Food and Drink" }, { "S" : "Bar" } ] 13001000
        // [ { "S" : "Travel" }, { "S" : "Lodging" }, { "S" : "Hotels and Motels" } ] 22012003
        // Find category for hotel and lodging.
        categoryIds: ["13005000", "13001000"], 
      };

      // ── Notification-direct pairs (runs BEFORE fuzzy matcher) ──────────────
      // Receipts uploaded via instant notification carry solid data (card +
      // amount + date). Pair them directly with their transactions and exclude
      // both from the fuzzy matcher's input — they don't need string similarity
      // scoring, and shouldn't be subject to it.
      const notifMatches = findNotificationDirectMatches(uploadedReceipts, syncedTransactions);
      const notifReceiptIds = new Set(notifMatches.map(m => m.id));
      const notifTxnIds     = new Set(notifMatches.map(m => m.matchedItem.id));

      if (notifMatches.length) {
        console.log(`📌 Notification-direct matches: ${notifMatches.length}`,
          notifMatches.map(m => ({
            receiptId:     m.id,
            receiptVendor: m.vendorName,
            txId:          m.matchedItem.id,
            txMerchant:    m.matchedItem.merchantEntityName,
            notifAmount:   m.capitalOneInstantNotification?.purchaseAmount,
            txAmount:      m.matchedItem.amount,
            flag:          m.flag,
          }))
        );
      }

      // const fuzzyReceiptInput    = uploadedReceipts.filter(r => !notifReceiptIds.has(r.id));
      const fuzzyReceiptInput = uploadedReceipts.filter(
        r => r.plaidCreditCardTransactionId &&
            r.plaidCreditCardTransactionId !== '0' &&
            !notifReceiptIds.has(r.id)
      );
      const fuzzyTransactionInput = syncedTransactions.filter(t => !notifTxnIds.has(t.id));
      // ── End notification-direct pairs ──────────────────────────────────────

      // Find matches using the utility function and update state
      const result = findMatchesWithQbVendors(fuzzyReceiptInput, fuzzyTransactionInput, qbVendors, config);
      console.log('🎯 magic results: ', result);

      // ── Admin-linked pairs ─────────────────────────────────────────────────
      // When an admin manually links an unmatched receipt to a transaction the
      // receipt gets a matchStatus like 'with-transaction-admin' or
      // 'with-notification-admin'. The algorithmic matcher above doesn't know
      // about these explicit links, so we inject them directly into matches here.
      //
      // We build a reverse lookup (receiptId → transaction) from syncedTransactions
      // so we can find the partner transaction for each admin-linked receipt.
      // const READY_ADMIN_STATUSES = new Set([
      //   'with-transaction-admin',
      //   'with-notification-admin',
      // ]);
      const READY_ADMIN_STATUSES = new Set([
        'with-transaction-admin',
        'with-notification-admin',
        'ready-for-approval',
      ]);

      const txByReceiptId = syncedTransactions.reduce((acc, tx) => {
        if (tx.capitalOneCreditCardTransactionReceiptId) {
          acc[tx.capitalOneCreditCardTransactionReceiptId] = tx;
        }
        return acc;
      }, {});

      const alreadyInMatches = new Set(result.matches.map(m => m.id));

      const adminPairs = uploadedReceipts
        .filter(r => READY_ADMIN_STATUSES.has(r.matchStatus) && !alreadyInMatches.has(r.id))
        .map(r => {
          const tx = txByReceiptId[r.id];
          if (!tx) {
            console.warn('⚠️ Admin-linked receipt has no matching transaction in syncedTransactions:', r.id, r.matchStatus);
            return null;
          }
          return { ...r, matchedItem: tx };
        })
        .filter(Boolean);

      if (adminPairs.length) {
        console.log(`✅ Injecting ${adminPairs.length} admin-linked pair(s) into Ready tab:`, adminPairs.map(p => ({ receiptId: p.id, matchStatus: p.matchStatus, txId: p.matchedItem?.id })));
        result.matches = [...result.matches, ...adminPairs];
      }
      // ── End admin-linked pairs ─────────────────────────────────────────────

      // Merge notification-direct matches into final result
      // if (notifMatches.length) {
      //   result.matches = [...result.matches, ...notifMatches];
      // }

      setMatchResults(result);
    }
  }, [uploadedReceipts, syncedTransactions, expenseCategories, qbVendors, refreshIndex]);

  const handleScroll = (e) => {
    const { scrollTop, scrollHeight, clientHeight } = e.target;
    if (scrollTop + clientHeight >= scrollHeight) {
      // future: load more
    }
  };

  const formatAmount = (amount) => {
    if (amount == null || amount === "") return null;

    let str = amount.toString().trim();

    // Handle accounting-style negatives like "599.14-"
    if (/^-?\d*\.?\d*-$/g.test(str)) {
      str = "-" + str.replace("-", "");
    } else if (str.endsWith("-")) {
      str = "-" + str.slice(0, -1);
    }

    // Remove all characters except digits, minus, dot, and comma
    str = str.replace(/[^0-9.,-]/g, "");

    // Ensure only the first minus is preserved
    str = str.replace(/(?!^)-/g, "");

    // Handle European comma as decimal
    if (str.includes(",") && str.includes(".")) {
      // Assume comma is thousands separator, remove it
      str = str.replace(/,/g, "");
    } else if (str.includes(",")) {
      str = str.replace(",", ".");
    }

    const num = parseFloat(str);
    if (isNaN(num)) return null;

    return parseFloat(num.toFixed(2)); // Return as number (not string)
  };

  const [autoMatchedReceipts, setAutoMatchedReceipts] = useState([]);
  useEffect(() => {
    if (!mappedTeamMembers.length || !uploadedUnmatchedReceipts.length || !transactionsByCard.length) {
      // console.warn("One or more arrays are empty. Skipping processing...");
      return;
    }

    if (
      mappedTeamMembers.length === 0 ||
      uploadedUnmatchedReceipts.length === 0 ||
      transactionsByCard.length === 0
    ) {
      // console.warn("⏳ Data is still loading... Waiting before running matching logic.");
      return;
    }

    // filter out capitalOneCreditCardTransactionReceiptId that are not null
    const filteredTransactionsByCard = transactionsByCard.map(card => ({
      ...card,
      transactions: card.transactions.filter(txn => !txn.capitalOneCreditCardTransactionReceiptId)
    }));
    // console.log('📌 mappedTeamMembers: ', mappedTeamMembers);

    // 🔍 DIAGNOSTIC LOG 0: Check raw mappedTeamMembers for duplicates
    const capitalOneMembers = mappedTeamMembers.filter(member => 
      member.qbChartOfAccounts?.qbChartOfAccountsFullyQualifiedName?.includes("Capital One Credit Card")
    );
    console.log(`📋 Capital One mapped members: ${capitalOneMembers.length}`);
    
    // Check for duplicate teamMemberId + cardLastFour combinations
    const memberCardCombos = capitalOneMembers.map(m => `${m.teamMemberId}-${m.cardLastFour}`);
    const uniqueCombos = new Set(memberCardCombos);
    if (memberCardCombos.length !== uniqueCombos.size) {
      console.warn("⚠️ DUPLICATE teamMemberId + cardLastFour combinations found!");
      const dupes = memberCardCombos.filter((combo, index) => memberCardCombos.indexOf(combo) !== index);
      console.warn("Duplicate combinations:", [...new Set(dupes)]);
      dupes.forEach(combo => {
        const [teamMemberId, card] = combo.split('-');
        const entries = capitalOneMembers.filter(m => m.teamMemberId === teamMemberId && m.cardLastFour === card);
        console.log(`  TeamMember ${teamMemberId}, Card ${card}: ${entries.length} entries`, 
          entries.map(e => ({ 
            id: e.id,
            chartOfAccount: e.qbChartOfAccounts?.qbChartOfAccountsFullyQualifiedName
          }))
        );
      });
    }

    // Step 1: Create a lookup for team members by teamMemberId
    const teamMemberLookup = mappedTeamMembers
      .filter(member => 
        member.qbChartOfAccounts?.qbChartOfAccountsFullyQualifiedName?.includes("Capital One Credit Card")
      )
      .reduce((acc, member) => {
        if (!acc[member.teamMemberId]) {
          acc[member.teamMemberId] = [];
        }
        // Prevent duplicate card numbers for the same team member
        if (!acc[member.teamMemberId].includes(member.cardLastFour)) {
          acc[member.teamMemberId].push(member.cardLastFour);
        }
        return acc;
      }, {});
    // 🔍 DIAGNOSTIC LOG 1: Check for duplicate cards in teamMemberLookup
    console.log("📌 Full teamMemberLookup:", teamMemberLookup);
    Object.entries(teamMemberLookup).forEach(([teamMemberId, cards]) => {
      if (cards.length !== new Set(cards).size) {
        console.warn(`⚠️ DUPLICATE CARDS FOUND for teamMemberId ${teamMemberId}:`, cards);
      }
    });
    
    // const targetId = "84259c64-9d28-407c-82fa-cd1d4389fd6f";

    // const targetReceipt = uploadedUnmatchedReceipts.find(r => r.id === targetId);
    // console.log("🎯 Raw receipt before filter/map:", targetReceipt);

    // Step 2: Enhance receipts with cardLastFour using teamMemberId (only if amountPaid exists)
    const receiptsWithCard = uploadedUnmatchedReceipts
      .filter(receipt => {
        const linked = receipt.plaidCreditCardTransactionId;
        if (linked && linked !== '0' && linked !== 'PENDING') return false;
        const amounts = [
          formatAmount(receipt.amountPaid),
          formatAmount(receipt.amountDue),
          formatAmount(receipt.statementTotal),
          formatAmount(receipt.capitalOneInstantNotification?.purchaseAmount)
        ];
        // return amounts.some(amount => typeof amount === 'number' && !isNaN(amount));
        return amounts.some(a => typeof a === 'number' && !isNaN(a));
      })
      .map(receipt => {
        const possibleCards = receipt.capitalOneInstantNotification?.lastFour
          ? [receipt.capitalOneInstantNotification.lastFour]
          : (teamMemberLookup[receipt.teamMemberId] || []);
        return {
          ...receipt,
          amountPaid: receipt.amountPaid ? formatAmount(receipt.amountPaid) : null,
          amountDue: receipt.amountDue ? formatAmount(receipt.amountDue) : null,
          statementTotal: receipt.statementTotal ? formatAmount(receipt.statementTotal) : null,
          capitalOneInstantNotification: receipt.capitalOneInstantNotification
            ? {
                ...receipt.capitalOneInstantNotification,
                purchaseAmount: receipt.capitalOneInstantNotification.purchaseAmount
                  ? formatAmount(receipt.capitalOneInstantNotification.purchaseAmount)
                  : null,
              }
            : null,
          possibleCards // Now we have all possible cards to check
        };
      });
    // Step 3: Create a lookup for transactions by accountNumber
    const transactionsByAccount = filteredTransactionsByCard.reduce((acc, card) => {
      acc[card.accountNumber] = card.transactions;
      return acc;
    }, {});

    // console.log("📌 receiptsWithCard:", receiptsWithCard);
    
    // 🔍 DIAGNOSTIC LOG 2: Find the "333extra Cheese LLC" receipt
    const cheeseReceipt = receiptsWithCard.find(r => 
      r.vendorName?.toLowerCase().includes('cheese') || 
      r.capitalOneInstantNotification?.purchaseMerchant?.toLowerCase().includes('cheese')
    );
    if (cheeseReceipt) {
      console.log("🧀 333extra Cheese LLC Receipt:", {
        id: cheeseReceipt.id,
        vendorName: cheeseReceipt.vendorName,
        amount: cheeseReceipt.amountPaid || cheeseReceipt.amountDue || cheeseReceipt.statementTotal,
        teamMemberId: cheeseReceipt.teamMemberId,
        possibleCards: cheeseReceipt.possibleCards,
        hasDuplicateCards: cheeseReceipt.possibleCards?.length !== new Set(cheeseReceipt.possibleCards).size
      });
    }


    // verify that transacion.amount is the correct element to match on for the tx side.
    // Step 4: Find matching transactions for each receipt
    const matched = receiptsWithCard
      .map(receipt => {
        if (!receipt.possibleCards?.length) return null; // Skip if no cards available

        // 🔍 DIAGNOSTIC LOG 3: Track processing for cheese receipt
        const isCheese = receipt.vendorName?.toLowerCase().includes('cheese');
        if (isCheese) {
          console.log("🔄 Processing cheese receipt, checking cards:", receipt.possibleCards);
        }

        // Check transactions for all possible cards
        for (const cardLastFour of receipt.possibleCards) {
          const transactions = transactionsByAccount[cardLastFour] || [];
          
          if (isCheese) {
            console.log(`  → Checking card ${cardLastFour}, ${transactions.length} transactions available`);
          }
          
          const matchedTransaction = transactions.find(tx => {
            const formattedTxAmount = formatAmount(tx.amount);
            const absFormattedTxAmount = Math.abs(formattedTxAmount);

            const notifAmt = formatAmount(receipt?.capitalOneInstantNotification?.purchaseAmount);
            const grat = formatAmount(receipt?.gratuity);

            const receiptAmounts = [
              notifAmt,
              (notifAmt != null && grat != null) ? Number((notifAmt + grat).toFixed(2)) : null,
              formatAmount(receipt?.amountPaid),
              formatAmount(receipt?.amountDue),
              formatAmount(receipt?.statementTotal),
            ].filter(a => a !== null);

            // const receiptAmounts = [
            //   formatAmount(receipt?.amountPaid),
            //   formatAmount(receipt?.amountDue),
            //   formatAmount(receipt?.statementTotal),
            //   formatAmount(receipt?.capitalOneInstantNotification?.purchaseAmount)
            // ].filter(amount => amount !== null);

            return receiptAmounts.some(receiptAmount => {
              const absReceiptAmount = Math.abs(receiptAmount);
              return absFormattedTxAmount === absReceiptAmount;
            });
          });

          if (matchedTransaction) {
            if (isCheese) {
              console.log(`  ✅ MATCH FOUND on card ${cardLastFour}:`, {
                txId: matchedTransaction.id,
                txAmount: matchedTransaction.amount,
                txStore: matchedTransaction.store
              });
            }
            return { 
              ...receipt, 
              matchedTransaction,
              cardLastFour // Include which card actually matched
            };
          }
        }
        
        return null; // No match found across all cards
      })
      .filter(Boolean);
    
    // 🔍 DIAGNOSTIC LOG 4: Check final matched results for duplicates
    console.log(`📊 Total matched receipts: ${matched.length}`);
    const matchedIds = matched.map(m => m.id);
    const uniqueIds = new Set(matchedIds);
    if (matchedIds.length !== uniqueIds.size) {
      console.warn("⚠️ DUPLICATE IDs IN MATCHED RESULTS!");
      const duplicates = matchedIds.filter((id, index) => matchedIds.indexOf(id) !== index);
      console.warn("Duplicate receipt IDs:", duplicates);
      
      // Show details for each duplicate
      duplicates.forEach(dupId => {
        const matches = matched.filter(m => m.id === dupId);
        console.log(`Receipt ${dupId} appears ${matches.length} times:`, matches.map(m => ({
          vendorName: m.vendorName,
          amount: m.amountPaid || m.amountDue,
          cardLastFour: m.cardLastFour,
          transactionId: m.matchedTransaction?.id
        })));
      });
    }
    
    setAutoMatchedReceipts(matched);

    // console.log("📌 All Receipt Amounts:", receiptsWithCard.map(r => r?.amountPaid));
    // console.log("📌 All Transaction Amounts:", transactionsByAccount["2491"]?.map(tx => tx?.amount));

    // console.info("✅  ----- Matched Receipts:", matched);
  }, [mappedTeamMembers, uploadedUnmatchedReceipts, transactionsByCard, refreshIndex]); // Dependencies


  const searchHeaderTextClass = useBreakpointValue({
    sm: 'dark-sfpro-heading-2',
    md: 'dark-sfpro-heading-2',
    lg: 'dark-sfpro-heading-1',
    xl: 'dark-sfpro-heading-1',
    '2xl': 'dark-sfpro-heading-1'
  })

  const [ selectedExpenseCategoryOption, setSelectedExpenseCategoryOption ] = useState('');
  const updateSelectedExpenseCategory = useCallback((event, index) => {
    console.log('📝 updateSelectedExpenseCategory called')
    console.log('event: ', event)
    console.log('index: ', index)
    // setIsIFTATracked(event?.isIFTATracked===true ? true : false)
    setSelectedExpenseCategoryOption(event||'');
    setValue('selectExpenseCategory', event.value||'')
    clearErrors('selectExpenseCategory')
  }, [setValue, clearErrors])

  const handleDetachReceipt = async (transactionId, receiptId) => {
    if (!transactionId || transactionId === '0' || transactionId === 'PENDING') {
      console.error('Detach aborted — invalid transactionId:', transactionId);
      toast({
        status: 'error',
        title: 'Cannot detach',
        description: 'No valid transaction linked to this receipt.',
        isClosable: true,
      });
      return;
    }
    const receipt = uploadedReceipts.find(r => r.id === receiptId);
    const restoreStatus = receipt?.capitalOneInstantNotificationId
      ? 'with-notification'
      : 'preemptive';

    const updateTransactionParams = {
      id: transactionId,
      status: 'pending_match',
      capitalOneCreditCardTransactionReceiptId: '',
      approvedById: '',
    };
    const transactionResults = await updateTransaction(updateTransactionParams);
    console.log('transactionResults: ', transactionResults);

    if (transactionResults !== transactionId) {
      toast({ status: 'error', title: 'Detach failed', description: 'Transaction not updated. Receipt left as-is.', isClosable: true });
      return;
    }

    const updateReceiptProps = {
      id: receiptId,
      matchStatus: restoreStatus,
      plaidCreditCardTransactionId: '0',
    };
    const receiptResults = await updateReceipt(updateReceiptProps);
    console.log('receiptResults: ', receiptResults);

    handleRefresh();
    onEditTransactionClose();

  }

  const [ currentEditTransaction, setCurrentEditTransaction ] = useState(null)
  const [ currentEditTransactionIndex, setCurrentEditTransactionIndex ] = useState(null)
  const handleOpenEditTransaction = (transaction, index) => {
    
    // 🔒 Hard gate on QuickBooks connection
    if (!canUseQbo) {
      console.warn("EditTransaction blocked: QuickBooks shared connection is not usable.");
      // optional: show a toast instead of silently failing
      // toast({
      //   status: "warning",
      //   title: "QuickBooks not ready",
      //   description: "You can’t edit this transaction until the QuickBooks shared connection is active.",
      // });
      return;
    }

    console.log('!   transaction: ', transaction)
    console.log('!   index: ', index)
    
    // Clear the previous pair's expense category first. Without this, a pair
    // with no category (or one not in the list) opens showing, and publishes
    // with, whatever category the last opened pair had.
    setSelectedExpenseCategoryOption('');
    setValue('selectExpenseCategory', '');

    // Initialize expense category when opening drawer
    if (transaction?.expenseCategoryId && expenseCategories.length) {
      const expense = expenseCategories.find((category) => category.value === transaction.expenseCategoryId);
      if (expense) {
        const expenseCategory = {
          value: expense.value,
          label: expense.label,
          isIFTATracked: expense?.isIFTATracked === true ? true : false,
        };
        console.log('💚 Setting initial expense category:', expenseCategory);
        setSelectedExpenseCategoryOption(expenseCategory);
        setValue('selectExpenseCategory', expenseCategory.value);
      }
    }
    
    setCurrentEditTransaction(transaction)
    setCurrentEditTransactionIndex(index)
  }

  useEffect(() => {
    if (currentEditTransaction) {
      onEditTransactionOpen();
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  },[currentEditTransaction])

  const onEditTransactionClose = () => {
    resetMatches();
    setSelectedExpenseCategoryOption('');
    setValue('selectExpenseCategory', '');
    setCurrentEditTransaction(null);
    // handleRefresh()
    editTransactionClose();
  }

  const [qbCustomers, setQBCustomers] = useState([]);

  useEffect(() => {
    const fetchQuickBooksCustomers = async () => {
      try {
        const customers = await getCustomersViaProxy({
          qbenv: qbEnv,
          app: "shared",
        });
        // console.info("QuickBooks customers via proxy:", customers);
        setQBCustomers(Array.isArray(customers) ? customers : []);
      } catch (err) {
        console.error("Failed to fetch QuickBooks customers via proxy:", err);
        setQBCustomers([]);
      }
    };

    if (qbEnv && canUseQbo) {
      fetchQuickBooksCustomers();
    }
  }, [qbEnv, canUseQbo, refreshIndex]);

  const generateInvoiceRow = (invoice, index) => {

    // console.log('invoice: ', invoice);

    let rowBackgroundColor = 'transparent';
    const signedUrls = invoice?.signedUrls||[];
    const invoiceNotes = invoice?.addedNotes||'';
    const invoiceReceiptId = invoice?.invoiceReceiptID||'';
    let invoiceReceiptDate = invoice?.invoiceReceiptDate||'';
    if (invoiceReceiptDate) {
      let formattedDate = awsFormatUnknownDate(invoiceReceiptDate);
      if (formattedDate) {
        invoiceReceiptDate = spellOutDate(formattedDate, 'apple-ny');
      }
    }
    let invoiceDueDate = invoice?.dueDate||'';
    if (invoiceDueDate) {
      let formattedDate = awsFormatUnknownDate(invoiceDueDate);
      if (formattedDate) {
        invoiceDueDate = spellOutDate(formattedDate, 'apple-ny');
      }
    }

    const invoiceVendorName = invoice?.vendorName||'';
    const titleCaseVendorName = invoiceVendorName
      .split(' ')
      .map(word => word.charAt(0).toUpperCase() + word.slice(1))
      .join(' ')||'N/A';

    let hasUnit = false;
    if (invoice.iftaFuelTaxTrackId || invoice.unitId || invoice.units?.items?.length > 0) {
      hasUnit = true;
    
      if (invoice?.iftaFuelTaxTrack?.unitId) {
        const matchedUnit = units?.find(unit => unit.id === invoice?.iftaFuelTaxTrack?.unitId);
        const iftaUnitDescription = matchedUnit ? `${matchedUnit.code} ${matchedUnit.type?.name}/${matchedUnit.subType?.name}` : '';
    
        // Ensure match.iftaFuelTaxTrack exists before assigning
        if (!invoice.iftaFuelTaxTrack) {
          invoice.iftaFuelTaxTrack = {}; // Initialize if undefined
        }
    
        invoice.iftaFuelTaxTrack.iftaUnit = iftaUnitDescription;
      }
    }

    const invoiceDivision = invoice?.division?.prettyname||'';
    const expenseCategoryId = invoice.expenseCategoryId||'';
    const expenseCategoryOption = getExpenseCategory(expenseCategoryId)||'';
    const expenseCategoryName = truncateString(expenseCategoryOption?.label,19)||'Unknown Expense';
    const expenseCategoryDivisionTooltipLabel = <>Expense Category: {expenseCategoryOption?.label}<br />Division: {invoiceDivision}</>;

    // amountDue, amountPaid, statementTotal
    const amountDuse = invoice?.amountDue||''; // invoice.AmountDue
    const amountPaid = invoice?.amountPaid||''; // invoice.AmountPaid
    const statementTotal = invoice?.statementTotal||''; // invoice.StatementTotal
    const displayAmount = (amountDuse) ? amountDuse : (amountPaid) ? amountPaid : (statementTotal) ? statementTotal : '-.--';

    let firstName = '';
    let lastName = '';
    let cardHolderName = ''
    let division = ''

    division = invoice?.teamMember?.division?.prettyname;
    const name = invoice?.teamMember;
    firstName = name.goesBy||name.firstName||'';
    lastName = name.lastName||'';
    cardHolderName = `${firstName} ${lastName}`

    const cardHolderNameTruncated = truncateString(cardHolderName, 10);
    const cardHolderTooltipLabel = <>User: {cardHolderName}<br />Division: {division}</>;

    return (
      <Fragment key={'transaction-frag_1_'+index}>
        <Tr
          key={`transaction-row-${index}`}
          _hover={{
            cursor: canUseQbo ? "pointer" : "not-allowed",
          }}
          bg={rowBackgroundColor}
          opacity={canUseQbo ? 1 : 0.6}
          onClick={
            canUseQbo
              ? () => handleOpenEditTransaction(invoice, index)
              : undefined
          }
        >
          <Td verticalAlign="top" py="10px">
            {/* Matched date on top-left */}
            <Text as="span" className="dark-sfpro-text-2">{invoiceReceiptDate||'N/A'}</Text>

            {/* Flex wrapper for alignment */}
            <Flex mt="8px" align="center" gap="12px">
              {/* Statement Type */}
              <Tooltip label={'Invoice'} placement="top" hasArrow>
                <Box display="inline-flex" alignItems="center">
                  <AvailableIcons boxSize="20px" iconRef="invoice" color="var(--dark-red-450)"/>
                </Box>
              </Tooltip>
              {/* IconButton */}
              {(signedUrls?.length > 0) && (
                <IconButton
                  backgroundColor={'transparent'}
                  h="24px"
                  w="24px"
                  variant="iconOnlyTertiaryTransparent"
                  icon={<AvailableIcons boxSize="20px" iconRef="image"/>}
                  onClick={(e) => {
                    e.stopPropagation(); // Prevent event bubbling
                    handleOpenGallery(signedUrls, {date: invoice.invoiceReceiptDate||'', 
                      supplier: '', amount: invoice?.amountDue||''});
                  }}
                />
              )}

              {/* ReceiptNotes icon */}
              {invoiceNotes && (
                <Tooltip label={invoiceNotes} placement="top" hasArrow>
                  <Box display="inline-flex" alignItems="center">
                    <AvailableIcons boxSize="20px" iconRef="notes" />
                  </Box>
                </Tooltip>
              )}
              {hasUnit && (
                <Tooltip label="Has Unit" placement="top" hasArrow>
                  <Box display="inline-flex" alignItems="center">
                    <AvailableIcons boxSize="20px" iconRef="iconUnits" />
                  </Box>
                </Tooltip>
              )}
            </Flex>
          </Td>
          <Td verticalAlign={'top'} py={'10px'}>
          
          <Text as="span" className='dark-sfpro-text-2'>{titleCaseVendorName}</Text>
            </Td>
          <Td verticalAlign={'top'} py={'10px'}>
            <Text as="span" className='dark-sfpro-text-2'>Invoice #: {invoiceReceiptId||'N/A'}<br/>Due Date: {invoiceDueDate||'N/A'}</Text>
          </Td>
          <Td verticalAlign={'top'} py={'10px'}>
            <Tooltip label={expenseCategoryDivisionTooltipLabel} placement="top" hasArrow>
              <Text as="span" className='dark-sfpro-text-2'>{expenseCategoryName}<br />{invoiceDivision}</Text>
            </Tooltip>
          </Td>
          <Td verticalAlign={'top'} align='right' py={'10px'}>
            <Tooltip placement="top" hasArrow>
              <Text as="span" className='dark-sfpro-text-2' color={'var(--error-red)'}>${displayAmount}</Text>
            </Tooltip>
          </Td>
          <Td verticalAlign={'top'} py={'10px'}>
            <Tooltip label={cardHolderTooltipLabel} placement="top" hasArrow>
              <Text as="span" className='dark-sfpro-text-2'>{cardHolderNameTruncated}</Text>
            </Tooltip>
          </Td>
          <Td verticalAlign={'middle'}>
            
          </Td>
        </Tr>
        <Tr key={`divider-row-${index}`}><Td colSpan={7} borderBottom={'1px solid var(--dark-module-divider)'}></Td></Tr>
      </Fragment>
    );
  }

  const GENERIC_MERCHANT_PATTERNS = [
    /^store$/i,
    /^merchant$/i,
    /^purchase$/i,
    /^retail$/i,
    /^pos/i,
    /^card\s+(purchase|transaction)$/i,
    /^unknown/i,
    /^payment$/i,
  ];

  const isLikelyGenericMerchant = (tx) => {
    const name = tx?.merchantEntityName?.trim();

    const nameLooksGeneric =
      !name || GENERIC_MERCHANT_PATTERNS.some(rx => rx.test(name));

    const lacksEntity =
      !tx?.merchantEntityId;

    const genericCategory =
      tx?.categoryId === '19000000';

    const noLocation =
      tx?.location &&
      Object.values(tx.location).every(v => v == null);

    // Require more than one signal to avoid false positives
    const signals = [
      nameLooksGeneric,
      lacksEntity,
      genericCategory,
      noLocation,
    ].filter(Boolean).length;

    return signals >= 2;
  };


  const generateProcessedRow = (match, index) => {

    // let statementType = 'receipt';
    let statementLabel = 'Receipt';
    let statementIcon = 'receipt';
    // let amountColor = 'var(--dark-text-grey-1)'; // Default color for receipts
    let showNegative = false; // Default to false for receipts
    let statementIconColor = 'var(--dark-gold-secondary)';
    if (match.transactionType === 'credit') {
      // statementType = 'credit';
      statementLabel = 'Credit';
      statementIcon = 'credit';
      statementIconColor = 'var(--success-green)';
      showNegative = true;
    }
    if (match.transactionType === 'refund') {
      // statementType = 'refund';
      statementLabel = 'Refund';
      statementIcon = 'credit';
      statementIconColor = 'var(--success-green)';
      showNegative = true;
    }
    if (match.transactionType === 'invoice') {
      // statementType = 'invoice';
      statementLabel = 'Invoice';
      statementIcon = 'invoice';
      statementIconColor = 'var(--error-red)';
    }

    if (match?.capitalOneInstantNotification?.purchaseDate || match?.invoiceReceiptDate) {
      if (match?.capitalOneInstantNotification?.purchaseDate) {
        match.formattedTransactionDate = awsFormatUnknownDate(match?.capitalOneInstantNotification?.purchaseDate)
      } else {
        match.formattedTransactionDate = awsFormatUnknownDate(match.invoiceReceiptDate);
      }
    }

    let hasUnit = false;
    if (match.iftaFuelTaxTrackId || match.unitId || match.units?.items?.length > 0) {
      hasUnit = true;
    
      if (match?.iftaFuelTaxTrack?.unitId) {
        const matchedUnit = units?.find(unit => unit.id === match?.iftaFuelTaxTrack?.unitId);
        const iftaUnitDescription = matchedUnit ? `${matchedUnit.code} ${matchedUnit.type?.name}/${matchedUnit.subType?.name}` : '';
    
        // Ensure match.iftaFuelTaxTrack exists before assigning
        if (!match.iftaFuelTaxTrack) {
          match.iftaFuelTaxTrack = {}; // Initialize if undefined
        }
    
        match.iftaFuelTaxTrack.iftaUnit = iftaUnitDescription;
      }
    }
    const matchedDate = spellOutDate(match.matchedItem.date, 'apple-ny');
    let merchantEntityName = match.matchedItem.merchantEntityName

    // Is this needed any longer?
    const matchedStore = match?.capitalOneInstantNotification?.purchaseMerchant||match.matchedItem.store||'Unknown';
    const merchantName = truncateString(matchedStore, 19);

    const receiptNotes = match?.addedNotes||'';
    const matchedMerchantName = truncateString(match.matchedItem.merchantEntityName,19);

    // // When qbSupplierMatched is false manual merging of QuickBooks supplier is needed.

    // const matchedCustomer = merchantEntityName ? qbCustomers.find(customer =>
    //   customer.CompanyName?.toLowerCase() === merchantEntityName.toLowerCase() ||
    //   customer.DisplayName?.toLowerCase() === merchantEntityName.toLowerCase()
    // ) : null;

    // if (matchedCustomer) {
    //     merchantEntityName = merchantEntityName+' (Vendor)';
    //   match.merchantEntityName = merchantEntityName;
    //   match.matchedItem.merchantEntityName = merchantEntityName;
    // }
    // let qbSupplierMatched = false;
    // let qbSupplierName = '';
    // let showVendorMap = false;
    // // Detect Plaid / network generic merchants early
    // const genericMerchantDetected = isLikelyGenericMerchant(match.matchedItem);

    // if (
    //   !genericMerchantDetected &&
    //   match?.qbVendor &&
    //   match?.qbVendor?.qbDisplayName?.toLowerCase() === merchantEntityName?.toLowerCase()
    // ) {
    //   qbSupplierMatched = true;
    //   qbSupplierName = merchantEntityName;
    //   showVendorMap = false;
    // } else {
    //   qbSupplierMatched = false;
    //   qbSupplierName = merchantEntityName || 'Unknown Vendor';
    //   showVendorMap = true;
    // }


    // if (match.matchedItem.merchantEntityName?.slice(-8) == "(Vendor)") {
    //   // Extract vendor name without "(Vendor)"
    //   const vendorName = match.matchedItem.merchantEntityName.replace(' (Vendor)', '').trim();
      
    //   // Look up vendor in qbVendors array
    //   const matchedQBVendor = qbVendors.find(vendor => 
    //     vendor.qbDisplayName === vendorName || 
    //     vendor.qbDisplayNameLower === vendorName.toLowerCase()
    //   );
      
    //   if (matchedQBVendor) {
    //     qbSupplierMatched = true;
    //     // Set the actual vendor object
    //     match.qbVendor = {
    //       qbId: matchedQBVendor.qbId,
    //       qbDisplayName: matchedQBVendor.qbDisplayName
    //     };
    //     qbSupplierName = matchedQBVendor.qbDisplayName;
    //     showVendorMap = false;
    //   } else {
    //     // If not found, it's actually NOT matched
    //     qbSupplierMatched = false;
    //     qbSupplierName = vendorName;
    //   }
    // }

    // match.qbSupplierMatched = qbSupplierMatched;
    // match.qbSupplierName = qbSupplierName;
    // match.showVendorMap = showVendorMap;

    // When qbSupplierMatched is false manual merging of QuickBooks supplier is needed.

    // Vestigial customer-detection — kept harmless for the handful of legacy
    // "(Vendor)" suffixed records. Does NOT gate the vendor lookup below.
    const matchedCustomer = merchantEntityName ? qbCustomers.find(customer =>
      customer.CompanyName?.toLowerCase() === merchantEntityName.toLowerCase() ||
      customer.DisplayName?.toLowerCase() === merchantEntityName.toLowerCase()
    ) : null;

    if (matchedCustomer) {
      merchantEntityName = merchantEntityName + ' (Vendor)';
      match.merchantEntityName = merchantEntityName;
      match.matchedItem.merchantEntityName = merchantEntityName;
    }

    let qbSupplierMatched = false;
    let qbSupplierName = '';
    let showVendorMap = false;
    const genericMerchantDetected = isLikelyGenericMerchant(match.matchedItem);

    // Strip any "(Vendor)" suffix before comparing against qbVendors
    const cleanedEntityName = merchantEntityName
      ? merchantEntityName.replace(/\s*\(Vendor\)\s*$/i, '').trim()
      : '';
    const cleanedLower = cleanedEntityName.toLowerCase();

    // Block 1: already-enriched match (fuzzy matcher set this). Accepted when it
    // is the same vendor by CANONICAL name (case, punctuation, &/And/N, and
    // payment/entity suffixes like "-Check" or "(ACH)" ignored), not just exact text.
    const cleanedCanonical = canonicalizeVendorName(cleanedEntityName);
    if (
      !genericMerchantDetected &&
      cleanedCanonical &&
      canonicalizeVendorName(match?.qbVendor?.qbDisplayName) === cleanedCanonical
    ) {
      qbSupplierMatched = true;
      qbSupplierName = match.qbVendor.qbDisplayName;
      showVendorMap = false;
    }
    // Block 2: enrich from qbVendors (covers notification-direct + admin-linked paths).
    // Auto-matches on TIER 1 only (canonical equality via the shared matcher).
    // Tier 2 prefix hits are left for a human in VendorMap.
    else if (!genericMerchantDetected && cleanedLower) {
      const nameMatch = matchQbVendorByName(cleanedEntityName, qbVendors);
      const matchedQBVendor = nameMatch.tier === 1 ? nameMatch.qbVendor : null;
      if (matchedQBVendor) {
        qbSupplierMatched = true;
        match.qbVendor = {
          qbId: matchedQBVendor.qbId,
          qbDisplayName: matchedQBVendor.qbDisplayName,
        };
        qbSupplierName = matchedQBVendor.qbDisplayName;
        showVendorMap = false;
      } else {
        qbSupplierName = cleanedEntityName || 'Unknown Vendor';
        showVendorMap = true;
      }
    } else {
      qbSupplierName = cleanedEntityName || merchantEntityName || 'Unknown Vendor';
      showVendorMap = true;
    }

    match.qbSupplierMatched = qbSupplierMatched;
    match.qbSupplierName = qbSupplierName;
    match.showVendorMap = showVendorMap;
    
    const qbSupplierNameTruncated = truncateString(qbSupplierName, 19);
    const receiptDivision = match?.division?.prettyname||'';
    const signedUrls = match?.signedUrls||[];
    
    const receiptHasIssue = match?.hasIssue||false;
    let infoIconColor = 'var(--success-green)';
    if (receiptHasIssue) {
      if (match?.matchStatus === 'fixed-issue') {
        infoIconColor = 'var(--success-green)';
      } else {
        infoIconColor = 'var(--error-red)';
      }
    }
    const receiptIssue = match?.issue||'';
    // const receiptStatus = match?.status||'';
    const receiptIssueFormatted = receiptIssue.split('::cr::').map((line, index) => (
      <Box key={index}>
        {line}
        {index < receiptIssue.split('::cr::').length - 1 && <br />}
      </Box>
    ))
    const purchaseAmount = formatCurrency(Math.abs(match?.capitalOneInstantNotification?.purchaseAmount));
    const matchedAmount = formatCurrency(Math.abs(match.matchedItem.amount));
    // const displayAmount = (purchaseAmount===matchedAmount || purchaseAmount==="$0.00") ? matchedAmount : <>{purchaseAmount}<br/>{matchedAmount}</>;
    const displayAmount = (purchaseAmount === matchedAmount || purchaseAmount === "$0.00")
      ? matchedAmount
      : <>{matchedAmount}<br/>{purchaseAmount}</>;   // transaction first, to match the merchant column

    const lastFour = match?.capitalOneInstantNotification?.lastFour||match.matchedItem.accountNumber||'';
    const cardHolder = mappedTeamMembers?.find(record => record.cardLastFour === lastFour);
    // console.log('cardHolder: ', cardHolder)
    let firstName = '';
    let lastName = '';
    let cardHolderName = ''
    let division = ''
    if (cardHolder?.teamMember) {
      division = cardHolder?.teamMember?.division?.prettyname;
      const name = cardHolder?.teamMember;
      firstName = name.goesBy||name.firstName||'';
      lastName = name.lastName||'';
      cardHolderName = `${firstName} ${lastName}`
    } else {
      cardHolderName = `${lastFour}`;
      // console.log('cardHolder?.teamMember: ', cardHolder)
    }

    const cardHolderNameTruncated = truncateString(cardHolderName, 10);
    const expenseCategoryId = match.expenseCategoryId||'';
    // console.log('🐞 expenseCategoryId: ', expenseCategoryId);
    const expenseCategoryOption = getExpenseCategory(expenseCategoryId)||'';
    // console.log('🐞 expenseCategoryOption: ', expenseCategoryOption);
    const expenseCategoryName = truncateString(expenseCategoryOption?.label,19)||'Unknown Expense';
    // console.log('🐞 expenseCategoryName: ', expenseCategoryName);
    const qbSupplierTooltipLabel = <>Supplier: {qbSupplierName}</>;

    const qbMerchantTooltipLabel = <>Merchant Name: {matchedMerchantName}</>;
    const cardHolderTooltipLabel = <>User: {cardHolderName}<br />Card: {lastFour}<br />Division: {division}</>;

    const purchaseValue = match?.capitalOneInstantNotification?.purchaseAmount||match.matchedItem.amount;
    const matchedValue = match.matchedItem.amount;
    const difference = Math.abs(purchaseValue - matchedValue);
    const higherAmount = Math.max(purchaseValue, matchedValue);
    const tipPercentage = (difference / higherAmount) * 100;
    const amountTooltipLabel = <>
      Receipt Amount: {purchaseAmount}<br />
      Billed Amount: {matchedAmount}<br />
      {(difference!==0) && `Difference: $${difference.toFixed(2)}`}
      {(difference!==0) && <br />}
      {(difference!==0) && `Tip Percentage: ${tipPercentage.toFixed(2)}%`}
      </>;
    const expenseCategoryDivisionTooltipLabel = <>Expense Category: {expenseCategoryOption?.label}<br />Division: {receiptDivision}</>;

    // Return the entire row element
    let rowBackgroundColor = 'transparent';
    return (
      <Fragment key={'transaction-frag_1_'+index}>
        <Tr
          key={`transaction-row-${index}`}
          _hover={{
            cursor: canUseQbo ? "pointer" : "not-allowed",
          }}
          bg={rowBackgroundColor}
          opacity={canUseQbo ? 1 : 0.6}
          onClick={
            canUseQbo
              ? () => handleOpenEditTransaction(match, index)
              : undefined
          }
        >
          <Td verticalAlign="top" py="10px">
            {/* Matched date on top-left */}
            <Text as="span" className="dark-sfpro-text-2">{matchedDate}</Text>

            {/* Flex wrapper for alignment */}
            <Flex mt="8px" align="center" gap="12px">
              {/* Statement Type receipt, credit, invoice*/}
              <Tooltip label={statementLabel} placement="top" hasArrow>
                <Box display="inline-flex" alignItems="center">
                  <AvailableIcons boxSize="20px" iconRef={statementIcon} color={statementIconColor}/>
                </Box>
              </Tooltip>
              {/* IconButton */}
              {(signedUrls?.length > 0) && (
                <IconButton
                  backgroundColor={'transparent'}
                  h="24px"
                  w="24px"
                  variant="iconOnlyTertiaryTransparent"
                  icon={<AvailableIcons boxSize="20px" iconRef="image"/>}
                  onClick={(e) => {
                    e.stopPropagation(); // Prevent event bubbling
                    handleOpenGallery(signedUrls, {date: match.matchedItem.date, 
                      supplier: qbSupplierName, amount: matchedAmount});
                  }}
                />
              )}

              {/* ReceiptNotes icon */}
              {receiptNotes && (
                <Tooltip label={receiptNotes} placement="top" hasArrow>
                  <Box display="inline-flex" alignItems="center">
                    <AvailableIcons boxSize="20px" iconRef="notes" />
                  </Box>
                </Tooltip>
              )}
              {hasUnit && (
                <Tooltip label="Has Unit" placement="top" hasArrow>
                  <Box display="inline-flex" alignItems="center">
                    <AvailableIcons boxSize="20px" iconRef="iconUnits" />
                  </Box>
                </Tooltip>
              )}
              {receiptHasIssue && (
                <Tooltip label={receiptIssueFormatted} placement="top" hasArrow>
                  <Box display="inline-flex" alignItems="center">
                    <AvailableIcons boxSize="20px" iconRef="info" color={infoIconColor}/>
                  </Box>
                </Tooltip>
              )}
            </Flex>
          </Td>
          <Td verticalAlign={'top'} py={'10px'}>
            {(!qbSupplierMatched) ? (
              <Tooltip px={'10px'} label={qbSupplierTooltipLabel} placement="top" hasArrow>
                <Badge variant="solid" borderRadius={'6px'} marginRight={'10px'} paddingTop={'5px'} paddingBottom={'5px'} colorScheme={'green'} textAlign="center" display="inline-block" minWidth="60px" textTransform="none">
                  {qbSupplierNameTruncated}
                </Badge>
              </Tooltip>
            ) : (
              <Tooltip label={qbSupplierTooltipLabel} placement="top" hasArrow>
                <Text as="span" className='dark-sfpro-text-2'>{qbSupplierNameTruncated}</Text>
              </Tooltip>)
            }
          </Td>
          <Td verticalAlign={'top'} py={'10px'}>
            <Tooltip label={qbMerchantTooltipLabel} placement="top" hasArrow>
              <Text as="span" className='dark-sfpro-text-2'>{matchedMerchantName}<br />{toTitleCase(merchantName)}</Text>
            </Tooltip>
            {match.matchConflict && (
              <Tooltip
                label="Another receipt is already attached to this transaction. Publishing is blocked until one of them is moved to the correct transaction."
                placement="top"
                hasArrow
              >
                <Badge
                  variant="solid"
                  borderRadius={'6px'}
                  marginLeft={'8px'}
                  paddingTop={'3px'}
                  paddingBottom={'3px'}
                  paddingLeft={'8px'}
                  paddingRight={'8px'}
                  colorScheme="red"
                  textTransform="none"
                >
                  Transaction already has a receipt
                </Badge>
              </Tooltip>
            )}
            {match.flag === 'weakMerchant' && (
              <Tooltip
                label="Merchant names differ between notification and transaction (often Plaid naming inconsistency). Card, amount, and date all match — safe to approve."
                placement="top"
                hasArrow
              >
                <Badge
                  variant="solid"
                  borderRadius={'6px'}
                  marginLeft={'8px'}
                  paddingTop={'3px'}
                  paddingBottom={'3px'}
                  paddingLeft={'8px'}
                  paddingRight={'8px'}
                  colorScheme="gray"
                  textTransform="none"
                >
                  Merchant differs
                </Badge>
              </Tooltip>
            )}
          </Td>
          <Td verticalAlign={'top'} py={'10px'}>
            <Tooltip label={expenseCategoryDivisionTooltipLabel} placement="top" hasArrow>
              <Text as="span" className='dark-sfpro-text-2'>{expenseCategoryName}<br />{receiptDivision}</Text>
            </Tooltip>
          </Td>
          <Td verticalAlign={'top'} align='right' py={'10px'}>
            <Tooltip label={amountTooltipLabel} placement="top" hasArrow><>
              {(showNegative === true) ? 
                <Text as="span" className='dark-sfpro-text-2' color={statementIconColor}>-{displayAmount}</Text> : 
                <Text as="span" className='dark-sfpro-text-2'>{displayAmount}</Text>}
            </></Tooltip>
            {match.flag === 'tipSuspected' && (
              <Tooltip
                label={`Notification: $${match.capitalOneInstantNotification?.purchaseAmount} · Transaction: $${match.matchedItem?.amount} — likely tip added between swipe and post. Verify against receipt image.`}
                placement="top"
                hasArrow
              >
                <Badge
                  variant="solid"
                  borderRadius={'6px'}
                  marginLeft={'8px'}
                  paddingTop={'3px'}
                  paddingBottom={'3px'}
                  paddingLeft={'8px'}
                  paddingRight={'8px'}
                  colorScheme="orange"
                  textTransform="none"
                >
                  Tip suspected — verify
                </Badge>
              </Tooltip>
            )}
          </Td>
          <Td verticalAlign={'top'} py={'10px'}>
            <Tooltip label={cardHolderTooltipLabel} placement="top" hasArrow>
              <Text as="span" className='dark-sfpro-text-2'>{cardHolderNameTruncated}</Text>
            </Tooltip>
          </Td>
          <Td verticalAlign={'middle'}>
            
          </Td>
        </Tr>
        <Tr key={`divider-row-${index}`}><Td colSpan={7} borderBottom={'1px solid var(--dark-module-divider)'}></Td></Tr>
      </Fragment>
    );
  };
  
  // CHART OF ACCOUNTS
  const [ dynamodbChartOfAccounts, setDynamoDBChartOfAccounts ] = useState([])
  

  const fetchAndProcessChartOfAccounts = async () => {
    let allSort = (qbEnv === 'sandbox') ? 'sandbox' : 'all';
    const ddbChartOfAccounts = await fetchAllChartOfAccounts(allSort);
    setDynamoDBChartOfAccounts(ddbChartOfAccounts);
    console.info(' --- DynamoDB Chart of Accounts:', ddbChartOfAccounts);

    let quickBooksChartOfAccounts;
    try {
      quickBooksChartOfAccounts = await getChartOfAccountsViaProxy({
        qbenv: qbEnv,
        app: "shared",
      });
    } catch (err) {
      console.error("getChartOfAccountsViaProxy failed:", err);
      alert("Unable to load QuickBooks Chart of Accounts. Check the shared QuickBooks connection.");
      return;
    }
    console.info(' --- quickBooksChartOfAccounts via proxy:', quickBooksChartOfAccounts);

    const { creates, updates, disables } = compareDatasets(
      ddbChartOfAccounts,
      quickBooksChartOfAccounts
    );

    if (creates.length > 0 || updates.length > 0 || disables.length > 0) {
      const cardSwaps = await updateDynamoDBChartOfAccounts(creates, updates, disables);

      // Re-fetch after sync
      const refreshed = await fetchAllChartOfAccounts(allSort);
      setDynamoDBChartOfAccounts(refreshed);
      await fetchMappedTeamMembers();

      // Alert admin if any card swaps were detected
      if (cardSwaps.length > 0) {
        cardSwaps.forEach(swap => {
          toast({
            title: 'Card Number Change Detected',
            description: `${swap.accountName}: card updated from ****${swap.oldLastFour} → ****${swap.newLastFour}. Please verify pending transactions for this cardholder.`,
            status: 'warning',
            duration: null, // stays until dismissed
            isClosable: true,
            position: 'top',
          });
        });
      }
    }
  };

  useEffect(() => {
    const fetchExpenseCategories = async () => {
      const results = await fetchQuickBooksExpenseCategories(qbEnv);
      // console.warn(' ----- fetchQuickBooksExpenseCategories results: ', results);
      // setDynamoDBExpenseCategories(results);
      
      const categories = results.map(expense => ({
        value: expense.qbId,
        label: expense.appName,
        isIFTATracked: (expense?.isIFTATracked===true) ? true : false,
      }));
      setExpenseCategories(categories);
    }

    fetchExpenseCategories();
  },[qbEnv, refreshIndex])

  // const compareExpenseCategoryDatasets = (dynamoDBData, quickBooksData) => {
  //   const updates = [];
  //   const creates = [];
  
  //   quickBooksData.forEach(qbItem => {
  //     const match = dynamoDBData.find(dynamoItem => dynamoItem.qbId === qbItem.Id);
  
  //     if (!match) {
  //       creates.push(qbItem);
  //     } else {
  //       updates.push(qbItem);
  //     }
  //   });
  
  //   return { creates, updates };
  // };
  
  // const updateDynamoDBExpenseCategories = async (creates, updates) => {
  //   for (const item of creates) {
  //     const input = {
  //       qbId: item.Id,
  //       allSort: (qbEnv === 'sandbox') ? 'sandbox' : 'all',
  //       acctNum: item.AcctNum,


  //       qbName: item.Name,
  //       qbFullyQualifiedName: item.FullyQualifiedName,
  //       qbActive: item.Active.toString(),
  //       qbParentId: item.ParentRef?.value||'',
  //       qbParentName: item.ParentRef?.name||'',
  //       qbLevel: item.Level.toString(),
  //       qbSubAccount: item.SubAccount.toString(),
  //       qbSpecialAccountType: item.SpecialAccountType||'',
        
  //     };

  //     try {
  //       const result = await addOrUpdateExpenseCategory(input);
  //       console.log('DynamoDB created:', result)
  //     } catch (error) {
  //       console.error('Error creating DynamoDB:', error);
  //     }
  //   }
  //   for (const item of updates) {
  //     const input = {
  //       qbId: item.Id,
  //       qbName: item.Name,
  //       qbFullyQualifiedName: item.FullyQualifiedName,
  //       qbActive: item.Active.toString(),
  //       qbParentId: item.ParentRef?.value||'',
  //       qbParentName: item.ParentRef?.name||'',
  //       qbLevel: item.Level.toString(),
  //       qbSubAccount: item.SubAccount.toString(),
  //       qbSpecialAccountType: item.SpecialAccountType||'',
  //       allSort: (qbEnv === 'sandbox') ? 'sandbox' : 'all',
  //     };

  //     try {
  //       const result = await addOrUpdateExpenseCategory(input);
  //       console.log('DynamoDB updated:', result)
  //     }
  //     catch (error) {
  //       console.error('Error updating DynamoDB:', error);
  //     }
  //   }
  // };
        
  const updateDynamoDBChartOfAccounts = async (creates, updates, disables = []) => {
    const cardSwaps = []; // track any card number changes detected

    for (const item of creates) {
      const input = {
        qbChartOfAccountsId: item.Id,
        qbChartOfAccountsName: item.Name,
        qbChartOfAccountsFullyQualifiedName: item.FullyQualifiedName,
        qbChartOfAccountsActive: item.Active.toString(),
        allSort: (qbEnv === 'sandbox') ? 'sandbox' : 'all',
      };
      try {
        const result = await addOrUpdateChartOfAccounts(input);
        console.log('DynamoDB created:', result);
      } catch (error) {
        console.error('Error creating DynamoDB:', error);
      }
    }

    for (const item of updates) {
      const input = {
        qbChartOfAccountsId: item.Id,
        qbChartOfAccountsName: item.Name,
        qbChartOfAccountsFullyQualifiedName: item.FullyQualifiedName,
        qbChartOfAccountsActive: item.Active.toString(),
        allSort: (qbEnv === 'sandbox') ? 'sandbox' : 'all',
      };
      try {
        const result = await addOrUpdateChartOfAccounts(input);
        console.log('DynamoDB updated:', result);

        // Card swap detection — did the last four digits change?
        const newLastFour = item.FullyQualifiedName?.match(/\b\d{4}\b/)?.[0];
        if (newLastFour) {
          const existingMapping = mappedTeamMembers.find(
            m => m.qbChartOfAccountsId === item.Id
          );
          if (existingMapping && existingMapping.cardLastFour !== newLastFour) {
            const oldLastFour = existingMapping.cardLastFour;
            console.warn(
              `Card swap detected for account ${item.Id} (${item.Name}): ` +
              `${oldLastFour} → ${newLastFour}. Updating mapping.`
            );
            try {
              await updateCreditCardTeamMember({
                qbChartOfAccountsId: item.Id,
                teamMemberId: existingMapping.teamMemberId,
                cardLastFour: newLastFour,
                isActive: existingMapping.isActive,
                isDefault: existingMapping.isDefault,
              });
              cardSwaps.push({
                accountId: item.Id,
                accountName: item.Name,
                oldLastFour,
                newLastFour,
              });
            } catch (swapError) {
              console.error('Error updating cardLastFour on mapping:', swapError);
            }
          }
        }
      } catch (error) {
        console.error('Error updating DynamoDB:', error);
      }
    }

    for (const item of disables) {
      const input = {
        qbChartOfAccountsId: item.qbChartOfAccountsId,
        qbChartOfAccountsName: item.qbChartOfAccountsName,
        qbChartOfAccountsFullyQualifiedName: item.qbChartOfAccountsFullyQualifiedName,
        qbChartOfAccountsActive: 'false',
        allSort: (qbEnv === 'sandbox') ? 'sandbox' : 'all',
      };
      try {
        const result = await addOrUpdateChartOfAccounts(input);
        console.log('DynamoDB disabled:', result);
      } catch (error) {
        console.error('Error disabling in DynamoDB:', error);
      }
    }

    return cardSwaps;
  };
  
  const compareDatasets = (dynamoDBData, quickBooksData) => {
    const updates = [];
    const creates = [];
    const disables = [];

    // Existing: walk QBO to find creates and updates
    quickBooksData.forEach(qbItem => {
      const match = dynamoDBData.find(dynamoItem => dynamoItem.qbChartOfAccountsId === qbItem.Id);
      if (!match) {
        creates.push(qbItem);
      } else if (!areObjectsEqual(match, qbItem)) {
        updates.push(qbItem);
      }
    });

    // NEW: walk DDB active records to find ones no longer in QBO
    const qbIdSet = new Set(quickBooksData.map(qbItem => qbItem.Id));
    dynamoDBData.forEach(ddbItem => {
      if (
        ddbItem.qbChartOfAccountsActive === 'true' &&
        !qbIdSet.has(ddbItem.qbChartOfAccountsId)
      ) {
        disables.push(ddbItem);
      }
    });

    return { creates, updates, disables };
  };
  
  const areObjectsEqual = (dynamoItem, qbItem) => {
    return (
      dynamoItem.qbChartOfAccountsId === qbItem.Id &&
      dynamoItem.qbChartOfAccountsName === qbItem.Name &&
      dynamoItem.qbChartOfAccountsFullyQualifiedName === qbItem.FullyQualifiedName &&
      dynamoItem.qbChartOfAccountsActive === qbItem.Active.toString()
    );
  };

  const removeTeamMemberMapEntry = (qbChartOfAccountsId) => {
    setValue(`selectTeamMember_${qbChartOfAccountsId}`, {label: '', id: ''})
    setSelectedTeamMemberMap((prev) => {
      const updatedMap = { ...prev };
      delete updatedMap[qbChartOfAccountsId];
      return updatedMap;
    });
  };

  const [ selectedTeamMemberMap, setSelectedTeamMemberMap ] = useState([])

  const updateSelectedTeamMemberMap = (
    qbChartOfAccountsId, 
    selectedTeamMember, 
    mappedTeamMemberCOAId
  ) => {

    const selectedTeamMemberId = selectedTeamMember || '';
    setSelectedTeamMemberMap((prev) => ({
      ...prev,
      [qbChartOfAccountsId]: selectedTeamMemberId
    }));

    console.log('qbChartOfAccountsId: ', qbChartOfAccountsId)
    console.log('mappedTeamMemberCOAId: ', mappedTeamMemberCOAId)
    console.log('selectedTeamMember: ', selectedTeamMember)

  }

  const updateTeamMemberMap = async (accountId, cardLastFour='') => {
    console.log('accountId: ', accountId)
    const isExistingMapping = mappedTeamMembers.some(mapping => mapping.qbChartOfAccountsId === accountId);
    console.log('mappedTeamMembers: ', mappedTeamMembers)
    console.log('isExistingMapping: ', isExistingMapping)
    let allSort = (qbEnv === 'sandbox') ? 'sandbox' : 'all';

    let stringWithCardNumber = isExistingMapping?.qbChartOfAccounts?.qbChartOfAccountsFullyQualifiedName;
    const match = stringWithCardNumber?.match(/\b\d{4}\b/); // Only calls .match if stringWithCardNumber is defined
    const lastFourDigits = match ? match[0] : cardLastFour;

    const input = {
      qbChartOfAccountsId: accountId,
      teamMemberId: selectedTeamMemberMap[accountId],
      cardLastFour: lastFourDigits,
      isActive: true,
      isDefault: true,
      allSort: allSort,
    };
    console.log(' ----- input -----: ', input)
    // console.log('mappedTeamMembers: ', mappedTeamMembers)
    if (isExistingMapping) {
      // update ddb then refresh
      try {
        await updateCreditCardTeamMember(input);
        fetchMappedTeamMembers();
      } catch (error) {
        console.error('Error updating DynamoDB:', error);
      }
    } else {
      // create new mapping
      try {
        await addCreditCardTeamMemberMap(input);
        fetchMappedTeamMembers();
      } catch (error) {
        console.error('Error creating DynamoDB:', error);
      }
    }
  }

  const [ allTeamMemberNames, setAllTeamMemberNames ] = useState([])
  const fetchAllTeamMemberNames = async () => {
    const fetchedTeamMembers = await fetchAllTeamMembers();
    console.log('raw fetchedTeamMembers[0]:', JSON.stringify(fetchedTeamMembers[0]));
    // console.log('fetchedTeamMembers: ', fetchedTeamMembers)
    const teamMembers = [{ id: '00000', firstName: 'Select Team Member', goesBy: '', lastName: '' }, ...fetchedTeamMembers];
    setAllTeamMemberNames(teamMembers);
    return teamMembers;
  };

  const [ adminUsers, setAdminUsers ] = useState([])
  useEffect(() => {
    if (currentUserIsGlobalAdmin || userIsCreditCardManager) {
      const fetchData = async () => {
        const teamMembers = await fetchAllTeamMemberNames();
        // console.log('teamMembers[1] memberOf:', teamMembers[1]?.memberOf);
        const mappedMembers = await fetchMappedTeamMembers();
 
        // Fetch all divisions so we can identify which are valid for DJT creation.
        // Only divisions with allSort === 'all' can have daily job tickets.
        // Divisions with allSort === 'special' (e.g. MWR-MGR) are management
        // divisions that have no job tickets and should be excluded from searches.
        let validDivisionIds = new Set();
        try {
          const allDivisions = await fetchAllDivisions();
          for (const div of (allDivisions || [])) {
            if (div.allSort === 'all') {
              validDivisionIds.add(div.id);
            }
          }
        } catch (err) {
          console.error('Failed to fetch divisions for cardholder view:', err);
          // If this fails, proceed without filtering — better to show too many
          // divisions than to silently show no DJTs for anyone.
        }
 
        const adminUserOptions = teamMembers
          .filter(tm => {
            const memberOf = tm.memberOf || [];
            return (
              tm.id !== '00000' &&
              tm.isActive === true &&
              (memberOf.includes('supervisor') || memberOf.includes('issued_credit_card'))
            );
          })
          .map(tm => {
            console.log('tm divisions:', tm.id, tm.divisionId, tm.multiDivisions);
            const rawMultiDivisions = tm.multiDivisions || [];
            const homeDivisionId = tm.divisionId || null;

            const divisionSet = new Set(rawMultiDivisions);
            if (homeDivisionId) divisionSet.add(homeDivisionId);

            const divisionsForSearch = (validDivisionIds.size > 0)
              ? [...divisionSet].filter(id => validDivisionIds.has(id))
              : [...divisionSet];

            if (tm.id === 'de23b85a-edf2-4a14-9668-ded622d8a856') {
              console.log('validDivisionIds has b95d4c14?', validDivisionIds.has('b95d4c14-f63e-4976-b48c-5dd4ee144dc8'));
              console.log('divisionsForSearch result:', divisionsForSearch);
            }

            const firstName = tm.goesBy || tm.firstName;
            const name = `${firstName} ${tm.lastName}`;
            return {
              label:             name,
              value:             tm.id,
              divisionsForSearch,
              name,
            };
          })
          .sort((a, b) => a.label.localeCompare(b.label));

          console.log('adminUserOptions count:', adminUserOptions.length);
 
        setAdminUsers(adminUserOptions);
        setMappedTeamMembers(mappedMembers);
        const chartOfAccounts = await fetchDynamoDbChartOfAccounts(mappedMembers);
        console.log('chartOfAccounts', chartOfAccounts);

        // ── Phase 1: cardholder summary counts ─────────────────────────────
        // Build card-last-four → teamMemberId lookup from the already-fetched
        // mappedMembers. A cardholder can have more than one card so multiple
        // entries may resolve to the same teamMemberId — that is expected.
        const cardLookup = {};
        for (const m of (mappedMembers || [])) {
          if (m.cardLastFour && m.teamMemberId) {
            cardLookup[m.cardLastFour] = m.teamMemberId;
          }
        }

        // Reverse lookup: teamMemberId → [cardLastFour, ...]
        // Used in handleSelectCardholder to filter transactions for a specific cardholder.
        const reverseLookup = {};
        for (const m of (mappedMembers || [])) {
          if (m.cardLastFour && m.teamMemberId) {
            if (!reverseLookup[m.teamMemberId]) reverseLookup[m.teamMemberId] = [];
            reverseLookup[m.teamMemberId].push(m.cardLastFour);
          }
        }
        setTeamMemberCardLookup(reverseLookup);
        setCardToTeamMember(cardLookup);

        await buildCardholderCounts(cardLookup);
        // ── end Phase 1 ─────────────────────────────────────────────────────
      };

      fetchData();
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentUserIsGlobalAdmin, userIsCreditCardManager]);

  const fetchMappedTeamMembers = async (tmId) => {
    if (currentUserIsGlobalAdmin || userIsCreditCardManager) {
      const mappedTeamMembers = await fetchAllCreditCardTeamMemberMaps(qbEnv);
      setMappedTeamMembers(mappedTeamMembers);
      return mappedTeamMembers;
    } else {
      const mappedTeamMembers = await fetchAccountsByTeamMember(tmId);
      setMappedTeamMembers(mappedTeamMembers);
      return mappedTeamMembers;
    }
  };

  useEffect(() => {
    const fetchTeamMemberMap = async (environment) => {
      const mappedTeamMembers = await fetchAllCreditCardTeamMemberMaps(environment);
      console.log(' --- --- mappedTeamMembers: ', mappedTeamMembers)
      setMappedTeamMembers(mappedTeamMembers);
    };
    if (qbEnv) {
      fetchTeamMemberMap(qbEnv);
    }
  },[qbEnv])

  const fetchDynamoDbChartOfAccounts = async (mappedTeamMembers) => {
    let allSort = (qbEnv === 'sandbox') ? 'sandbox' : 'all';
    const ddbChartOfAccounts = await fetchAllChartOfAccounts(allSort);
    // console.warn('ddbChartOfAccounts: ', ddbChartOfAccounts);

    // Initialize the selectedTeamMemberMap with existing mappings
    const initialMap = {};
    // Only initialize map entries for active accounts
    ddbChartOfAccounts
      .filter(account => account.qbChartOfAccountsActive === 'true')
      .forEach(account => {
        initialMap[account.qbChartOfAccountsId] = '';
      });

    mappedTeamMembers.forEach(mapping => {
      initialMap[mapping.qbChartOfAccountsId] = mapping.teamMemberId;
    });

    setSelectedTeamMemberMap(initialMap);
    setDynamoDBChartOfAccounts(ddbChartOfAccounts); // keep full list in state; filter at render
    return ddbChartOfAccounts;
  };

  const generateChartOfAccountsRows = (accounts, mappedtms) => {
    
    // console.log('accounts: ', accounts)
    // console.log(' -=====- mappedtms: ', mappedtms)
    if (!accounts || !mappedtms) return null;
    // return accounts?.map((item, index) => {
    return accounts?.map((item, index) => {
      const mappedMember = mappedtms.find(mapping => mapping.qbChartOfAccountsId === item.qbChartOfAccountsId);

      let selectedTeamMember = '';
      if (mappedMember && selectedTeamMemberMap[item?.qbChartOfAccountsId] && selectedTeamMemberMap[item?.qbChartOfAccountsId] !== mappedMember?.teamMemberId) {
        const matchedTeamMember = allTeamMemberNames.find(tm => tm.id === selectedTeamMemberMap[item?.qbChartOfAccountsId]);
        const matchedGoesBy = (matchedTeamMember?.goesBy) ? `"${matchedTeamMember?.goesBy}"` : '';
        const matchedName = `${matchedTeamMember?.firstName} ${matchedGoesBy} ${matchedTeamMember?.lastName}`;
        selectedTeamMember = {label: matchedName, value: `${matchedTeamMember?.id}`};
        // console.info(' -11111- selectedTeamMember: ', selectedTeamMember)
      } else if (mappedMember?.teamMember) {
        const mappedGoesBy = (mappedMember?.teamMember?.goesBy) ? `"${mappedMember?.teamMember?.goesBy}"` : '';
        const mappedName = `${mappedMember?.teamMember?.firstName} ${mappedGoesBy} ${mappedMember?.teamMember?.lastName}`;
        selectedTeamMember = {label: mappedName, value: mappedMember?.teamMemberId};
        // console.info(' -22222- selectedTeamMember: ', selectedTeamMember)
      } else {
        // console.info(' -33333- selectedTeamMember: ', item?.qbChartOfAccountsId)
      }
      
      
      // let showButtonCell = false;
      const teamMemberIsMapped = (selectedTeamMemberMap[item?.qbChartOfAccountsId]?.length>0) ? true : false
      const selectedTeamMemberCOAId = selectedTeamMemberMap[item?.qbChartOfAccountsId]
      const mappedTeamMemberCOAId = mappedMember?.qbChartOfAccountsId
      const accountCOAId = item?.qbChartOfAccountsId
      const mappedTeamMemberId = mappedMember?.teamMemberId

      const cardNumberMatch = item?.qbChartOfAccountsName?.match(/\b\d{4}\b/);
      const cardLastFour = cardNumberMatch ? cardNumberMatch[0] : null;

      // console.info(' -!!!!!- selectedTeamMember: ', selectedTeamMember)

      return (
        <Fragment key={`frag_${index}`}>
          <Tr key={`tr_${index}`}>
            <Td>{item?.qbChartOfAccountsName}</Td>
            <Td>
              <Box>
                <FormSelectSimple
                  key={`teamMember_${accountCOAId}`}
                  register={register}
                  control={control}
                  errors={errors}
                  isRequired={true}
                  fieldName={`selectTeamMember_${accountCOAId}`}
                  placeholder={'Select Team Member'}
                  optionsArray={allTeamMemberNames?.map((tm, index) => ({
                    key: index,
                    value: tm.id,
                    label: tm.goesBy?.length>0 
                      ? `${tm.firstName} "${tm.goesBy}" ${tm.lastName}` 
                      : `${tm.firstName} ${tm.lastName}`
                  }))}
                  onChange={(e) => updateSelectedTeamMemberMap(accountCOAId, e?.value, mappedTeamMemberCOAId)}
                  // onChange={(e) => updateSelectedTeamMemberMap(accountCOAId, e, mappedTeamMemberCOAId)}
                  selectedoption={selectedTeamMember}
                />
              </Box>
            </Td>
            <Td>
              {(teamMemberIsMapped) && 
                <Box display={'flex'} gap={'5px'}>
                  {((selectedTeamMemberCOAId 
                      && mappedTeamMemberCOAId !== accountCOAId) 
                      || mappedTeamMemberId !== selectedTeamMemberMap[accountCOAId])
                    && 
                    <ButtonPrimaryPlain 
                      type="button"
                      name='updateTeamMemberMap'
                      value={isSubmitting ? 'Updating...' : 'Update'}
                      isDisabled={isSubmitting===true}
                      onClick={handleSubmit(() => {
                        // updateTeamMemberMap(accountCOAId);
                        updateTeamMemberMap(accountCOAId, cardLastFour  || '');
                      }, onError)}
                    />
                  }
                  {((selectedTeamMemberCOAId 
                      && mappedTeamMemberCOAId !== accountCOAId) 
                      || mappedTeamMemberId !== selectedTeamMemberMap[accountCOAId])
                    && <ButtonPrimaryPlain 
                    type="button"
                    name='resetTeamMemberMap'
                    value={'Cancel'}
                    isDisabled={isSubmitting===true}
                    onClick={handleSubmit(() =>{
                      removeTeamMemberMapEntry(accountCOAId);
                    })}
                  />}
                </Box>
              }
            </Td>
          </Tr>
          <Tr><Td borderBottom={'1px solid var(--dark-module-divider)'}></Td></Tr> 
        </Fragment>
      );
    });
  }
  // CHART OF ACCOUNTS

  const updateSelectedQBClass = (divisionName, qbClassOptions) => {
    console.log('🐞 updateSelectedQBClass divisionName: ', divisionName);
    console.log('🐞 updateSelectedQBClass qbClassOptions: ', qbClassOptions);
    // This really needs a mapping table created that can be synced with the QB classes
    let divisionNameNormalized = divisionName.replace(/[^a-zA-Z0-9]/g, '').toLowerCase();
    console.log('🐞 divisionNameNormalized: ', divisionNameNormalized);
    // this is a hack to normalize the division names to match the QB classes
    if (divisionNameNormalized === 'overheadadministration') {
      divisionNameNormalized = 'overheadadmin';
    }
    
    // QB side
    console.log('🐞 qbClassOptions: ', qbClassOptions)
    const matchedClass = qbClassOptions.find(option => option.label === divisionNameNormalized);
    console.log('🐞 matchedClass: ', matchedClass)
    return matchedClass;
  }

  // Creating QuickBooks Purchase
  // purchaseObject, filename, key, identityid, token, environment
  const pushStatementToQB = async (purchaseObj) => {
    const uploadResults = await pushStatementToQuickBooks(purchaseObj);
    // The API Gateway wrapper returns { statusCode, headers, body } where body is a JSON string.
    // Unwrap it so callers get the actual payload directly.
    if (uploadResults?.body && typeof uploadResults.body === 'string') {
      try {
        return JSON.parse(uploadResults.body);
      } catch {
        // body wasn't valid JSON — return as-is
      }
    }
    return uploadResults;
  }

  const handlePublish = async (transaction) => {
    // ── Early exit for non-publish actions ───────────────────────────────────
    // Return-to-cardholder and other status updates are pure DynamoDB writes.
    // They must bypass all QBO vendor validation and publishing logic.
    if (transaction.matchStatus === 'has-issue') {
      try {
        await updateCreditCardTransactionReceipt({
          id: transaction.id,
          matchStatus: 'has-issue',
          hasIssue: true,
          issue: transaction.issue,
          ...(transaction.expenseCategoryId && { expenseCategoryId: transaction.expenseCategoryId }),
        });
        setRefreshIndex(prev => prev + 1);
      } catch (err) {
        console.error('Failed to return receipt to cardholder:', err);
        toast({
          title: 'Error',
          description: 'Failed to return receipt to cardholder. Please try again.',
          status: 'error',
          duration: 5000,
          isClosable: true,
        });
      }
      return;
    }

    // console.log('🔍 FORM DATA CHECK in handlePublish:');
    // console.log('1. transaction.matchStatus value:', transaction.matchStatus);
    // console.log('2. Should be "published"');
    // console.log('3. Full transaction data:', JSON.stringify(transaction, null, 2));

    // console.log('transaction object structure:', Object.keys(transaction));

    // console.log('🔍 PUBLISH START - Vendor information:', transaction);
    // console.log('1. Transaction qbVendorId:', transaction.qbVendorId);
    // console.log('2. Transaction qbVendor:', transaction.qbVendor);
    // console.log('3. Transaction qbSupplierMatched:', transaction.qbSupplierMatched);
    
    // // Debug: Log full vendor state
    // console.log('=== TRANSACTION VENDOR STATE ===', JSON.stringify({
    //   transactionId: transaction.id,
    //   merchantName: transaction.merchantName,
    //   qbSupplierMatched: transaction.qbSupplierMatched,
    //   qbVendorId: transaction.qbVendorId,
    //   qbVendor: transaction.qbVendor,
    //   hasVendorId: !!transaction.qbVendorId,
    //   hasVendorObject: !!transaction.qbVendor,
    //   vendorNameFromObject: transaction.qbVendor?.qbDisplayName
    // }, null, 2));

    // ── Required fields, checked before anything is created or sent ──────────
    // QuickBooks accepts a purchase with no expense category or class, so an
    // incomplete publish would otherwise go through. Stop here instead, before
    // a vendor is created or the transaction is claimed. The class is looked
    // up from the division later; this only confirms a division is chosen.
    // Receipts being saved with an issue skip this, as they are not published.
    if (!transaction.hasIssue) {
      const hasExpenseCategory = !!transaction.selectExpenseCategory;
      const hasDivision = !!(
        transaction?.selectDivision?.label ||
        transaction?.division?.prettyname ||
        transaction?.divisionName
      );
      if (!hasExpenseCategory || !hasDivision) {
        const missing = [
          !hasExpenseCategory && 'an expense category',
          !hasDivision && 'a division',
        ].filter(Boolean).join(' and ');
        toast({
          status: 'error',
          title: 'Not published',
          description: `Choose ${missing}, then publish again. Nothing was sent to QuickBooks.`,
          duration: 10000,
          isClosable: true,
        });
        setIsPublishingToQuickBooks(false);
        return;
      }
    }

    // Get qbVendorId from transaction state (set by VendorMap)
    const isSupplierMatched = transaction.qbSupplierMatched;
    let qbVendorId = transaction.qbVendorId || transaction.qbVendor?.qbId;

    // Validation - check if vendor is selected OR if there's a new vendor name to use
    if (!qbVendorId && !isSupplierMatched) {
      // Check if there's a merchant name to use for creating a new vendor
      const hasNewVendorName = transaction.merchantName || transaction.newVendorName;
      
      if (!hasNewVendorName) {
        console.error('❌ No vendor selected and no merchant name available');
        alert('Please select a vendor from the VendorMap section before publishing.');
        setIsPublishingToQuickBooks(false);
        return;
      }
      
      // Allow publishing with new vendor name
      console.log('✅ Publishing with NEW vendor:', hasNewVendorName);
    }
    
    if (!qbVendorId && isSupplierMatched) {
      console.error('❌ Vendor marked as matched but no vendor ID found');
      alert('Vendor issue: Please select a vendor from the VendorMap section.');
      setIsPublishingToQuickBooks(false);
      return;
    }

    if (qbVendorId) {
      console.log('✅ Using existing vendor:', {
        id: qbVendorId,
        name: transaction.qbVendor?.qbDisplayName || 'Unknown',
        fromTransaction: true
      });
    } else {
      console.log('✅ Using NEW vendor:', {
        name: transaction.merchantName || transaction.newVendorName,
        willCreateInQuickBooks: true
      });
    }
    
    // ===== ADD THIS ENTIRE BLOCK HERE =====
    // Create vendor in QuickBooks if it doesn't exist
    if (!qbVendorId) {
      const newVendorName = transaction.merchantName || transaction.newVendorName;
      
      if (!newVendorName) {
        alert('Unable to determine vendor name. Please contact support.');
        setIsPublishingToQuickBooks(false);
        return;
      }
      
      // Before creating, check the already-loaded vendor list with the SAME
      // matcher every other vendor lookup uses. This stops publish from
      // spawning near-duplicate vendors in QBO ("Diamond Gas N Wash" vs
      // "Diamond Gas And Wash", "GenServ" vs "GenServ (ACH)", "Cherokee Hose &
      // Supply" vs "Cherokee Hose And Supply-Check") — the loop that produced
      // the stale/merged vendor rows in the first place.
      //   Tier 1 (canonical equality) -> reuse silently.
      //   Tier 2 (token-prefix)       -> ask; never create a likely duplicate unprompted.
      const nameMatch = matchQbVendorByName(newVendorName, qbVendors || []);
      let existingVendorMatch = nameMatch.tier === 1 ? nameMatch.qbVendor : null;

      if (!existingVendorMatch && nameMatch.tier === 2) {
        const listed = nameMatch.candidates
          .slice(0, 5)
          .map((v) => `  • ${v.qbDisplayName}`)
          .join('\n');
        const more = nameMatch.candidates.length > 5 ? `\n  …and ${nameMatch.candidates.length - 5} more` : '';
        const createAnyway = window.confirm(
          `"${newVendorName}" isn't selected, and QuickBooks has similar vendors:\n\n${listed}${more}\n\n` +
          `Cancel to go back and pick the right one from the vendor search.\n` +
          `OK only if this is a genuinely NEW vendor that should be created in QuickBooks.`
        );
        if (!createAnyway) {
          setIsPublishingToQuickBooks(false);
          return;
        }
      }

      if (existingVendorMatch?.qbId) {
        console.log('♻️ Reusing existing vendor instead of creating a duplicate:', {
          qbId: existingVendorMatch.qbId,
          name: existingVendorMatch.qbDisplayName,
          requestedName: newVendorName,
        });
        qbVendorId = existingVendorMatch.qbId;
        transaction.qbVendorId = qbVendorId;
        transaction.qbSupplierMatched = true;
        transaction.qbVendor = {
          qbId: existingVendorMatch.qbId,
          qbDisplayName: existingVendorMatch.qbDisplayName,
        };
      } else {
        console.log('📝 Creating new vendor in QuickBooks:', newVendorName);

        try {
          // Create vendor via proxy - matching syncQBVendors pattern
          const createVendorResponse = await createVendorViaProxy({
            qbenv: qbEnv,
            app: "shared",
            id: qbEnv === "sandbox" ? "primary-shared-sandbox" : "primary-shared",
            vendorName: newVendorName,
          });

          console.log('🎉 Vendor creation response:', createVendorResponse);

          // Extract vendor ID from response
          qbVendorId = createVendorResponse?.Vendor?.Id || createVendorResponse?.Id;

          if (!qbVendorId) {
            throw new Error('No vendor ID returned from QuickBooks');
          }

          console.log('✅ New vendor created with ID:', qbVendorId);

          // Update transaction object with new vendor info
          transaction.qbVendorId = qbVendorId;
          transaction.qbSupplierMatched = true;
          transaction.qbVendor = {
            qbId: qbVendorId,
            qbDisplayName: newVendorName
          };

          // Save vendor to DynamoDB (matching the syncQBVendors pattern)
          try {
            await addQuickBooksVendor({
              id: qbVendorId,
              name: newVendorName
            });
            console.log('✅ Vendor saved to database');

          } catch (dbError) {
            console.warn('⚠️ Failed to save vendor to database (non-critical):', dbError);
          }

        } catch (error) {
          console.error('❌ Failed to create vendor in QuickBooks:', error);
          const errorMsg = error?.message || String(error);

          // Check if it's a duplicate name error (non-fatal)
          if (errorMsg.includes("Duplicate Name Exists Error") || errorMsg.includes('"code":"6240"')) {
            console.warn('⚠️ Vendor already exists in QuickBooks, will search for existing ID');
            alert(`Vendor "${newVendorName}" already exists in QuickBooks. Please select it from the vendor list or sync vendors.`);
          } else {
            alert(`Failed to create vendor "${newVendorName}" in QuickBooks. Error: ${errorMsg}`);
          }

          setIsPublishingToQuickBooks(false);
          return;
        }
      }
    }

    if (!canUseQbo || !qbEnv) {
      alert("QuickBooks shared connection is not active. Please reconnect QuickBooks from the Admin dashboard.");
      return;
    }
    
    console.warn('handlePublish transaction: ', transaction);
    setIsPublishingToQuickBooks(true);

    if (transaction.hasIssue) {
      const results = await updateReceipt(transaction);
      console.log('updateReceipt results: ', results);
      setIsPublishingToQuickBooks(false);
      handleRefresh();
      onEditTransactionClose();
      return;
    }

    try {
      // Get accountRefId from mappedTeamMembers
      // Support both patterns: combined tmIdCardNumber OR separate accountNumber + teamMemberId
      let cardLastFour, teamMemberId;
      
      if (transaction.tmIdCardNumber) {
        // Original pattern: split the combined string
        const parts = transaction.tmIdCardNumber.split('#');
        teamMemberId = parts[0];
        cardLastFour = parts[1];
      } else {
        // Refactored pattern: use separate properties
        teamMemberId = transaction.teamMemberId;
        cardLastFour = transaction.accountNumber;
      }
      
      const qbAccount = mappedTeamMembers.find(
        record => 
          record.cardLastFour === cardLastFour && 
          record.teamMemberId === teamMemberId
      );
      const accountRefId = qbAccount?.qbChartOfAccountsId;
      
      console.log('🔍 Account mapping lookup:', { 
        cardLastFour, 
        teamMemberId,
        qbAccount, 
        accountRefId 
      });
      
      if (!accountRefId) {
        alert(`Unable to find QuickBooks account mapping for card ${cardLastFour} and team member. Please map this card in the admin section.`);
        setIsPublishingToQuickBooks(false);
        return;
      }

      // At this point, qbVendorId should already be set from the validation at the top
      // If not, we have a problem
      if (!qbVendorId) {
        alert('Unable to determine vendor. Please contact support.');
        setIsPublishingToQuickBooks(false);
        return;
      }

      console.log('🔍 Final vendor ID for QB:', qbVendorId);

      // ── Pre-publish vendor validation (fail-open) ───────────────────────────
      // The local vendor mirror has no active/deleted flag, so the matched
      // qbVendorId can point at a vendor that was deactivated in QBO. Sending
      // that to createQBPurchase yields a QBO 400 ("...has been deleted") which
      // the proxy flattens into an unactionable 500. Verify the vendor is alive
      // FIRST, and if it isn't, stop with a clear, copyable message instead.
      //
      // This is deliberately fail-open: any error in the check itself (network,
      // proxy, unknown_operation if the Lambda op isn't deployed yet) is logged
      // and we proceed exactly as before. The check can only ADD a clean stop on
      // a definitively dead vendor; it can never introduce a new blocking path.
      // QBO's own display name for the vendor this receipt is posted to, captured
      // from the check below. Used after a successful publish to keep the local
      // vendor mirror in sync. Stays null if the check fails open.
      let qboVendorDisplayName = null;
      try {
        const vendorStatus = await getVendorViaProxy({
          qbenv: qbEnv,
          app: "shared",
          vendorId: qbVendorId,
        });

        if (vendorStatus?.found && vendorStatus?.active && vendorStatus.displayName) {
          qboVendorDisplayName = vendorStatus.displayName;
        }

        if (vendorStatus && (vendorStatus.found === false || vendorStatus.active === false)) {
          const attemptedName =
            transaction.qbVendor?.qbDisplayName ||
            vendorStatus.displayName ||
            transaction.merchantName ||
            "(unknown)";

          console.error("🛑 Pre-publish vendor validation failed:", {
            qbVendorId,
            attemptedName,
            vendorStatus,
          });

          const vendorReport =
            `Vendor not usable in QuickBooks\n` +
            `Time: ${new Date().toISOString()}\n` +
            `Vendor: ${attemptedName}\n` +
            `qbId: ${qbVendorId}\n` +
            `Status: ${vendorStatus.found === false ? "not found" : "inactive/deleted"}\n` +
            `Receipt: ${transaction.id}`;

          const vendorToastId = `vendor-invalid-${transaction.id}`;
          toast({
            id: vendorToastId,
            status: "error",
            duration: null, // stays until dismissed
            isClosable: true,
            render: () => (
              <Box bg="red.50" border="1px solid" borderColor="red.200" borderRadius="md" p={3}>
                <Text fontWeight="bold" mb={1} color="red.700">
                  Can&rsquo;t publish &mdash; vendor is inactive in QuickBooks
                </Text>
                <Text fontSize="sm" mb={2}>
                  The vendor <b>{attemptedName}</b> (id {qbVendorId}) has been deleted or
                  deactivated in QuickBooks, so this receipt can&rsquo;t be posted to it.
                  Restore that vendor in QuickBooks, or pick a different vendor in the
                  VendorMap section, then publish again. Nothing was sent to QuickBooks.
                </Text>
                <Box
                  as="pre"
                  fontFamily="mono"
                  fontSize="xs"
                  bg="white"
                  border="1px solid"
                  borderColor="gray.200"
                  borderRadius="md"
                  p={2}
                  mb={3}
                  whiteSpace="pre-wrap"
                  wordBreak="break-word"
                >
                  {vendorReport}
                </Box>
                <Flex gap={2}>
                  <Button
                    size="sm"
                    colorScheme="red"
                    onClick={(e) => {
                      const btn = e.currentTarget;
                      const done = () => {
                        const orig = btn.innerText;
                        btn.innerText = "Copied ✓";
                        setTimeout(() => { btn.innerText = orig; }, 2000);
                      };
                      if (navigator?.clipboard?.writeText) {
                        navigator.clipboard.writeText(vendorReport).then(done).catch(() => {});
                      } else {
                        const ta = document.createElement("textarea");
                        ta.value = vendorReport;
                        document.body.appendChild(ta);
                        ta.select();
                        try { document.execCommand("copy"); done(); } catch (err) { /* noop */ }
                        document.body.removeChild(ta);
                      }
                    }}
                  >
                    Copy details
                  </Button>
                  <Button
                    size="sm"
                    variant="ghost"
                    onClick={() => toast.close(vendorToastId)}
                  >
                    Dismiss
                  </Button>
                </Flex>
              </Box>
            ),
          });

          setIsPublishingToQuickBooks(false);
          return;
        }
      } catch (vendorCheckErr) {
        // Fail-open: never let the validation call itself block a publish.
        console.warn(
          "⚠️ Pre-publish vendor validation could not run; proceeding without it:",
          vendorCheckErr
        );
      }

      // Get qbClass from division name
      const divisionName = transaction?.selectDivision?.label || 
                          transaction?.division?.prettyname || 
                          transaction?.divisionName || null;
      console.log('🐞  Division name:', divisionName);
      
      const qbClassesData = await getClassesViaProxy({
        qbenv: qbEnv,
        app: "shared",
      });
      console.log('🐞 qbClassesData via proxy:', qbClassesData);
      const qbClasses = qbClassesData.map((qbclass) => ({
        value: qbclass.Id,
        label: qbclass.Name.replace(/[^a-zA-Z0-9]/g, '').toLowerCase(),
      }));
            
      let qbClass = '';
      if (divisionName) {
        qbClass = updateSelectedQBClass(divisionName, qbClasses);
      }
      console.log('🐞  QB Class:', qbClass);

      // The division must map to a QuickBooks class. Checked before the
      // transaction is claimed, so a refusal here changes nothing.
      if (!qbClass?.value) {
        toast({
          status: 'error',
          title: 'Not published',
          description: divisionName
            ? `The division "${divisionName}" doesn't match a class in QuickBooks, so this can't be published. Choose a different division, or add the class in QuickBooks. Nothing was sent to QuickBooks.`
            : 'Choose a division, then publish again. Nothing was sent to QuickBooks.',
          duration: 12000,
          isClosable: true,
        });
        return;
      }

      let showFuelInQB = false;
      let showIFTAInQB = false;
      if (transaction?.expenseCategoryLabel) {
        const { expenseCategoryLabel } = transaction;

        const fuelPrefixes = ['442', '444', '446'];
        const isFuelCategory = fuelPrefixes.some(prefix =>
          expenseCategoryLabel.startsWith(prefix)
        );
        showFuelInQB = isFuelCategory;

        const iftaPrefixes = ['Fuel:OTR'];
        const isIFTAFuelCategory = iftaPrefixes.some(prefix =>
          expenseCategoryLabel.startsWith(prefix)
        );
        showIFTAInQB = isIFTAFuelCategory;
      }

      const iftaFuelTaxTrackId = transaction?.iftaFuelTaxTrackId||null;
      const receiptId = transaction?.id||null;
      const iftaUnitId = transaction?.iftaUnitId||null;
      const iftaTeamMemberId = transaction?.iftaTeamMemberId||null;
      const iftaState = transaction?.iftaState?.value||null;
      const iftaGallons = transaction?.iftaGallons||null;
      const iftaOdometer = transaction?.iftaOdometer||null;
      const statementDate = transaction?.transactionDate||null;
      let iftaExpenseDescription = '';
      let unitUsedDescription = '';
      if (transaction?.unitNamesString?.length>0) {
        unitUsedDescription = `Unit(s): ${transaction?.unitNamesString}`;
      }

      // 1) Prepare IFTA tracking payload (but do not call updateIFTAFuelTracking yet)
      let iftaTrackingJSON = null;
      if (showIFTAInQB) {
        iftaExpenseDescription = `
          Unit: ${transaction?.selectOTRUnit?.label || ''}
          State: ${transaction?.iftaState?.label || ''}
          Gallons: ${transaction?.iftaGallons || ''}
          Odometer: ${transaction?.iftaOdometer || ''}
          `.trim();

        iftaTrackingJSON = {
          id: iftaFuelTaxTrackId,
          statementDate,
          transactionId: receiptId,
          unitId: iftaUnitId,
          teamMemberId: iftaTeamMemberId,
          stateTwoChar: iftaState,
          gallons: iftaGallons,
          odometer: iftaOdometer
        }
      }

      // 2) Prepare transaction update
      // Normalize the statement type once. selectStatementType is a form-only
      // field and can be empty when publishing straight from the Ready tab, so
      // fall back to the persisted receipt.transactionType, then to 'receipt'.
      // This mirrors the || 'receipt' fallback the QBO push already uses (below)
      // so the status logic can never silently degrade to a non-published value.
      const statementType =
        transaction?.selectStatementType ||
        transaction?.transactionType ||
        'receipt';

      // The published status value is shared by BOTH the transaction.status
      // field and the receipt.matchStatus field so the two records stay in sync.
      const publishedStatus =
        statementType === 'invoice' ? 'published-invoice' :
        statementType === 'credit'  ? 'published-credit'  :
        statementType === 'refund'  ? 'published-refund'  :
        'published';

      const updateTransactionParams = {
        id: transaction.plaidCreditCardTransactionId,
        status: publishedStatus,
        approvedById: currentTeamMemberId,
        capitalOneCreditCardTransactionReceiptId: transaction.id,
        ...(transaction.capitalOneInstantNotificationId && { 
          capitalOneInstantNotificationId: transaction.capitalOneInstantNotificationId 
        })
      };
      
      // 3) Prepare receipt update props
      const props = {
        matchStatus: publishedStatus,
        id: transaction.id,
        issue: transaction?.issue||'',
        expenseCategoryId: transaction.selectExpenseCategory,
        plaidCreditCardTransactionId: transaction.plaidCreditCardTransactionId,
        ...(transaction.capitalOneInstantNotificationId && { 
          capitalOneInstantNotificationId: transaction.capitalOneInstantNotificationId 
        })
      };

      if (transaction?.selectClient||transaction?.clientId) {
        props.clientId = transaction?.selectClient||transaction?.clientId;
      }

      props.hasIssue = transaction?.hasIssue 
      ? (transaction?.hasIssue === 'true' || transaction?.hasIssue === true)
      : false;

      // debug:
      console.log('📎 uploadedDocuments for publish:', uploadedDocuments);
      console.log('📎 files JSON being sent:', JSON.stringify(
        uploadedDocuments.map(doc => ({
          fileKey: doc.key,
          fileName: doc.key.split('/').pop(),
          bucket: doc.bucket,
        }))
      ));

      // create the purchase/bill in QuickBooks.
      const purchaseParams = {
        // files: transaction.uploadedDocumentsJSON,
        files: JSON.stringify(
          uploadedDocuments.map(doc => ({
            fileKey: doc.key,
            fileName: doc.key.split('/').pop(),
            bucket: doc.bucket,
          }))
        ),
        identityId: transaction.identityKey,
        qbenv: qbEnv,
        accountId: transaction.accountNumber,
        accountRefId,
        vendorId: qbVendorId,
        txnAmount: transaction.amount,
        expensCatId: transaction.selectExpenseCategory,
        classId: qbClass?.value,
        memo: transaction.addedNotes || null,
        txnDate: transaction.transactionDate,
        statementType: statementType,
        amountDue: transaction?.amountDue||'',
        invoiceNumber: transaction?.invoiceReceiptNumber||'',
        dueDate: transaction?.dueDate||'',
      }

      purchaseParams.description =
        showIFTAInQB ? iftaExpenseDescription
        : showFuelInQB ? unitUsedDescription
        : null;

      if (transaction.clientQbId) {
        purchaseParams.customerId = transaction.clientQbId;
      }
      
      // Reserve the transaction for this receipt BEFORE anything is sent to
      // QuickBooks. If another receipt already owns it, stop here: publishing
      // would otherwise create the QuickBooks expense and then overwrite the
      // other receipt's link, stranding it (Oct 2026, QuikTrip $17.13).
      if (transaction.plaidCreditCardTransactionId) {
        const preClaim = transaction.matchConflict
          ? { ok: false, reason: 'TAKEN' }
          : await claimTransactionForReceipt({
              transactionId: transaction.plaidCreditCardTransactionId,
              receiptId: transaction.id,
            });
        if (!preClaim.ok) {
          toast({
            status: 'error',
            title: 'Not published',
            description: describeClaimFailure(preClaim) + ' Nothing was sent to QuickBooks.',
            duration: 12000,
            isClosable: true,
          });
          setIsPublishingToQuickBooks(false);
          return;
        }
      }

      const qbResults = await pushStatementToQB(purchaseParams);
      console.log("qbResults: ", qbResults);

      // Normalize result — pushStatementToQB unwraps the API Gateway envelope,
      // so qbResults should already be the parsed payload {ok, txnId, ...}
      const ok = qbResults?.ok === true;
      // const partial = qbResults?.partial === true;

      if (!ok) {
        console.error("QuickBooks publish failed or partial:", qbResults);

        // Save for debug panel
        setLastQbError(qbResults);

        // Map to friendly toast
        const { status, title, description } = mapQbErrorToFriendly(qbResults);

        toast({
          status,
          title,
          description,
          isClosable: true,
          duration: 12000,
        });

        // IMPORTANT: do NOT touch Dynamo if QB failed or is only partial
        setIsPublishingToQuickBooks(false);
        return;
      }

      // If we get here, QB succeeded fully
      setLastQbError(null); // clear last error on success

      // Dev record mode: nothing was sent to QuickBooks. Show what would have
      // been sent; the records below are still updated, with a RECORDED- id.
      if (qbResults?.recorded) {
        window.lastQboRecord = qbResults;
        console.groupCollapsed(`🧾 QuickBooks ${qbResults.entityType} recorded, not sent`);
        console.log('Request body:', JSON.stringify(qbResults.request?.body, null, 2));
        console.log('Attachments:', qbResults.attachments?.wouldAttach);
        if (qbResults.warnings?.length) console.warn('Warnings:', qbResults.warnings);
        console.groupEnd();
        toast({
          status: qbResults.warnings?.length ? 'warning' : 'info',
          title: 'Recorded, not sent to QuickBooks',
          description:
            `Dev record mode: the ${qbResults.entityType} was built but not sent. ` +
            `${qbResults.attachments?.total || 0} image(s) checked. ` +
            (qbResults.warnings?.length ? `Check: ${qbResults.warnings.join(' ')} ` : '') +
            'The full request is in the browser console (window.lastQboRecord).',
          duration: 15000,
          isClosable: true,
        });
      }

      // ==== ONLY HERE DO WE COMMIT TO DYNAMO ====

      // 0) Keep local vendor table in sync — fire-and-forget, never blocks publish.
      // Writes ONLY QBO's own name for the vendor id actually posted to
      // (qbVendorId), as returned by the pre-publish check. It never writes the
      // ERP's cached name or the bank merchant string, which could overwrite a
      // name the nightly reconcile had already corrected. If the check failed
      // open (no authoritative name), skip: the nightly reconcile covers it.
      if (qbVendorId && qboVendorDisplayName) {
        upsertQBVendorLocal({
          qbId: String(qbVendorId),
          qbDisplayName: qboVendorDisplayName,
        }).catch(err => console.warn('⚠️ vendor upsert non-critical failure:', err));
      }

      // 1) Update or create IFTA tracking if needed
      if (iftaTrackingJSON) {
        console.info("iftaTrackingJSON: ", iftaTrackingJSON);
        if (iftaFuelTaxTrackId) {
          // Existing IFTA record — update it
          const iftaResults = await updateIFTAFuelTracking(iftaTrackingJSON);
          console.warn("IFTA update results: ", iftaResults);
        } else {
          // No existing record — admin changed non-IFTA to IFTA, create one
          const newIftaId = await addIFTAFuelTracking({
            allSort: 'all',
            transactionId: iftaTrackingJSON.transactionId,
            unitId: iftaTrackingJSON.unitId,
            teamMemberId: iftaTrackingJSON.teamMemberId,
            stateTwoChar: iftaTrackingJSON.stateTwoChar,
            gallons: iftaTrackingJSON.gallons,
            odometer: iftaTrackingJSON.odometer,
          });
          console.warn("IFTA create results, new id: ", newIftaId);
          props.iftaFuelTaxTrackId = newIftaId;
        }
      }

      // 2) Update the credit-card transaction status + link back to receipt
      console.log("🟢 About to update TRANSACTION with params:", updateTransactionParams);
      // Same guard as the pre-claim above; it passes because the transaction
      // already points at this receipt. Kept guarded so nothing can overwrite
      // a different receipt's link between the two writes.
      let updateTransactionresults;
      if (updateTransactionParams.id) {
        // claimTransactionForReceipt sets id and the receipt link itself, so
        // the params can be passed through as they are.
        const claimResult = await claimTransactionForReceipt({
          transactionId: updateTransactionParams.id,
          receiptId: transaction.id,
          fields: updateTransactionParams,
        });
        updateTransactionresults = claimResult.ok ? claimResult.id : (claimResult.error || claimResult);
      } else {
        updateTransactionresults = await updateTransaction(updateTransactionParams);
      }
      console.log("🟢 Transaction update complete:", updateTransactionresults);

      // The wrapper returns the updated record id (a string) on success, or a
      // non-string ([err] / error object) on failure — it never throws. Verify
      // explicitly so a failed status write can't pass silently like it did before.
      const transactionUpdated = updateTransactionresults === updateTransactionParams.id;

      // 3) Update the receipt: flip matchStatus to the published value and store
      //    the QuickBooks expense id. statementType is always one of
      //    receipt/invoice/credit/refund now, so this always runs on success.
      props.quickbooksExpenseId = qbResults.txnId;

      console.log("🟢 About to update RECEIPT with props:", props);
      const results = await updateReceipt(props);
      console.log("🟢 Receipt update complete:", results);

      const receiptUpdated = results === props.id;

      // ---- Error capture ----------------------------------------------------
      // The wrappers never throw: on success they return the record id (a
      // string); on failure they return the caught value, which is usually the
      // Amplify GraphQLResult ({ data, errors:[{message, errorType, path}] }),
      // sometimes wrapped in an array ([err]). Dig the real message out of
      // whatever shape comes back so the toast and logs show the actual cause
      // instead of "incomplete".
      const describeWriteFailure = (res) => {
        const e = Array.isArray(res) ? res[0] : res;
        const gqlErrors = e?.errors;
        if (Array.isArray(gqlErrors) && gqlErrors.length) {
          return gqlErrors
            .map((g) => `${g.errorType ? `[${g.errorType}] ` : ""}${g.message || JSON.stringify(g)}${g.path ? ` (path: ${g.path.join(".")})` : ""}`)
            .join(" | ");
        }
        return e?.message || (typeof e === "string" ? e : JSON.stringify(e));
      };

      if (!transactionUpdated || !receiptUpdated) {
        const txnDetail = transactionUpdated ? "ok" : describeWriteFailure(updateTransactionresults);
        const rcptDetail = receiptUpdated ? "ok" : describeWriteFailure(results);

        // Full structured dump to the console for debugging. Everything needed
        // to reconcile or reproduce is here in one object.
        console.error("❌ PUBLISH STATUS WRITE FAILURE", {
          qbExpenseId: qbResults?.txnId,
          transactionId: updateTransactionParams.id,
          receiptId: props.id,
          statementType,
          publishedStatus,
          approvedById: currentTeamMemberId,
          transactionWrite: txnDetail,
          receiptWrite: rcptDetail,
          rawTransactionResult: updateTransactionresults,
          rawReceiptResult: results,
          transactionParams: updateTransactionParams,
          receiptProps: props,
        });

        const whichFailed =
          !transactionUpdated && !receiptUpdated ? "the transaction and receipt records"
          : !transactionUpdated ? "the transaction record"
          : "the receipt record";

        toast({
          status: "warning",
          title: "Published to QuickBooks, but status update incomplete",
          description:
            `QBO succeeded (expense ${qbResults?.txnId}) but ${whichFailed} did not confirm the status change. ` +
            `This pair may still show in the Ready tab. Do NOT re-publish — it would create a duplicate in QuickBooks. ` +
            `Please screenshot this and send it over.\n\n` +
            `Transaction (${updateTransactionParams.id}): ${txnDetail}\n` +
            `Receipt (${props.id}): ${rcptDetail}`,
          isClosable: true,
          duration: null, // stays on screen until the user dismisses it
        });
      }

    } catch (error) {
      console.error("Error publishing transaction:", error);
      toast({
        status: "error",
        title: "Unexpected error",
        description: error.message || "Something went wrong while publishing.",
        isClosable: true,
      });
    } finally {
      setIsPublishingToQuickBooks(false);
      handleRefresh();
      onEditTransactionClose();
    }
  };


  const updateReceipt = async (props) => {
    try {
        const response = await updateCreditCardTransactionReceipt(props);
        return response;
    } catch (error) {
      console.error('Error updating receipt:', error);
    }
  };

  const [sortConfig, setSortConfig] = useState({ key: '', direction: 'asc' });

  const onSortSubmittedReceipts = (field) => {
    let direction = 'asc';
    if (sortConfig.key === field && sortConfig.direction === 'asc') {
      direction = 'desc'; // Toggle to descending if already sorted ascending
    }
  
    setSortConfig({ key: field, direction });

    // Sorting logic based on field and direction
    const sortedData = [...matchResults.matches].sort((a, b) => {
      let valA, valB;
  
      // Custom sorting logic for different fields
      if (field === 'card') {
        // valA = a.capitalOneInstantNotification.lastFour;
        // valB = b.capitalOneInstantNotification.lastFour;
        valA = a.matchedItem.accountNumber;
        valB = b.matchedItem.accountNumber;
      } else if (field === 'supplier') {
        valA = a.qbVendor?.qbDisplayName;
        valB = b.qbVendor?.qbDisplayName;
      } else if (field === 'expense-category') {
        valA = a?.expenseCategoryId;
        valB = b?.expenseCategoryId;
      } else if (field === 'merchant') {
        valA = a.matchedItem.merchantEntityName;
        valB = b.matchedItem.merchantEntityName;
      } else if (field === 'date') {
        valA = new Date(a.matchedItem.date);
        valB = new Date(b.matchedItem.date);
      } else if (field === 'amount') {
        // Remove $ and commas, then parse as float for numeric comparison
        const parseAmount = (amount) => {
          // Handle cases where amount is a string (e.g., "$10.91")
          if (typeof amount === 'string') {
            return parseFloat(amount.replace(/[^0-9.-]+/g, ''));
          }
          return amount; // If already a number, return as is
        };
        valA = parseAmount(a.capitalOneInstantNotification.purchaseAmount);
        valB = parseAmount(b.capitalOneInstantNotification.purchaseAmount);
      } else {
        valA = a[field];
        valB = b[field];
      }
  
      // Compare values based on the direction
      if (direction === 'asc') {
        return valA > valB ? 1 : valA < valB ? -1 : 0;
      } else {
        return valA < valB ? 1 : valA > valB ? -1 : 0;
      }
    });

    console.log('sortedData: ', sortedData)
  
    // Update only the matches array in the state while preserving other arrays
    setMatchResults((prevResults) => ({
      ...prevResults,  // Preserve the other arrays (unmatchedFromArray1, unmatchedFromArray2)
      matches: sortedData,  // Update only the matches array with sorted data
    }));
  };

  const [ currentEditTransactionVendor, setCurrentEditTransactionVendor ] = useState(null)
  // revisit this later, a lot has changed how the receipts approval is displayed and how the vendor map is integrated, may not need to track the index of the transaction in the matchResults anymore if we can just update the transaction object directly and trigger a re-render that way
  const currentEditTransactionVendorIndex = 0
  // const [ currentEditTransactionVendorIndex, setCurrentEditTransactionVendorIndex ] = useState(null)
  // const handleOpenEditTransactionVendor = (transaction, index) => {
  //   // console.log('handleOpenEditTransactionVendor: ', transaction);
  //   setCurrentEditTransactionVendor(transaction);
  //   setCurrentEditTransactionVendorIndex(index);
  // }

  useEffect(() => {
    currentEditTransactionVendor && onEditTransactionVendorOpen();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  },[currentEditTransactionVendor])

  const onEditTransactionVendorClose = () => {
    setCurrentEditTransactionVendor(null)
    editTransactionVendorClose()
  }
    
  const mapQBVendorToStatementVendor = useCallback((vendorObj, index) => {
    console.info(' --- 🤖 --- vendorObj: ', vendorObj)
    console.log('index: ', index)

    if (vendorObj?.id && vendorObj?.name) {

      setValue('qbVendorId', vendorObj.id||'');
      setValue('merchantName', vendorObj.name);
      setValue('qbSupplierMatched', true);
      clearErrors('merchantName'); // Clear any merchant name errors
      currentEditTransaction.qbSupplierMatched = true;
      currentEditTransaction.qbSupplierName = vendorObj.name;
      currentEditTransaction.qbVendor = {
        qbId: vendorObj.id,
        qbDisplayName: vendorObj.name,
      }

      trigger();
      console.log(' 🪩 currentEditTransaction: ', currentEditTransaction)
    } else {
      //handle for new supplier?
    }
  }, [setValue, clearErrors, trigger, currentEditTransaction]);

  // function to filter results based on searchterm
  const [ showSearchReset, setShowSearchReset ] = useState(false);
  const filterMatchedData = (e) => {
    // console.log('filterMatchedData: ', e.target.value)
    const searchTerm = e.target.value;
    const searchTermLength = searchTerm.length;

    console.log('searchTerm: ', searchTerm)
    console.log('searchTermLength: ', searchTermLength)
    
    if (searchTermLength > 0) {
      setShowSearchReset(true);
    }
    const filteredResults = matchResults.matches.filter((match) => {
      
      const merchantName = match?.matchedItem?.merchantEntityName?.toLowerCase();
      const supplierName = match?.qbVendor?.qbDisplayName?.toLowerCase()||'';
      const amount1 = match?.capitalOneInstantNotification?.purchaseAmount?.toString();
      const amount2 = match?.matchedItem?.amount?.toString();
      const card1 = match?.capitalOneInstantNotification?.lastFour?.toString();
      const card2 = match?.matchedItem?.accountNumber?.toString();
      const searchLower = searchTerm.toLowerCase();

      return (
        merchantName?.includes(searchLower) ||
        supplierName?.includes(searchLower) ||
        amount1?.includes(searchLower) ||
        amount2?.includes(searchLower) ||
        card1?.includes(searchLower) ||
        card2?.includes(searchLower)
      );
    });

    console.log('filteredResults: ', filteredResults)
    setMatchResults((prevResults) => ({
      ...prevResults,
      matches: filteredResults,
    }));
  }

  const resetMatches = () => {
    setValue('searchInput', '')
    setRefreshIndex(prev => prev + 1)
    setShowSearchReset(false);
  }

  useEffect(() => {
    resetMatches();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  },[])

  const handleFileParsed = (jsonObject) => {
    console.log("Parsed JSON Object:", jsonObject);
    // Do something with the parsed JSON
  };

  const [ isMatching, setIsMatching ] = useState(false)

  const matchTransactionForApproval = async (data, { refresh = true } = {}) => {
    // console.log('🔵 MATCH START - data:', data);
    // console.log('🔵 Receipt ID:', data?.id);
    // console.log('🔵 Transaction ID:', data?.matchedTransaction?.id);
    // console.log('🔵 Instant Notification ID:', data?.capitalOneInstantNotificationId);

    setIsMatching(true);

    let creditCardTransactionId = data?.matchedTransaction?.id;
    let currentReceiptId = data?.id;
    let instantNotificationId = data?.capitalOneInstantNotificationId;
    
    if (!currentReceiptId || !creditCardTransactionId) {
      console.error('❌ Missing receipt ID or transaction ID');
      setIsMatching(false);
      return;
    }
    
    try {
      // 1. Claim the transaction first. Succeeds only if it has no receipt yet
      //    (or already has this one); otherwise nothing else is written.
      const claim = await claimTransactionForReceipt({
        transactionId: creditCardTransactionId,
        receiptId: currentReceiptId,
        fields: {
          status: 'pending_match',
          ...(instantNotificationId && { capitalOneInstantNotificationId: instantNotificationId }),
        },
      });
      console.log('🔵 Transaction claim result:', claim);
      if (!claim.ok) {
        toast({
          status: 'error',
          title: 'Not matched',
          description: describeClaimFailure(claim),
          duration: 10000,
          isClosable: true,
        });
        return;
      }

      // 2. Update the receipt
      const statementParams = {
        id: currentReceiptId,
        matchStatus: "ready-for-approval",
        plaidCreditCardTransactionId: creditCardTransactionId,
        ...(instantNotificationId && { capitalOneInstantNotificationId: instantNotificationId }),
      };

      console.log('🔵 About to update RECEIPT with:', statementParams);
      const updateReceiptResult = await updateCreditCardTransactionReceipt(statementParams);
      console.log('🔵 Receipt update results:', updateReceiptResult);

      // 3. Update the instant notification if it exists
      if (instantNotificationId) {
        const notificationParams = {
          id: instantNotificationId,
          capitalOneCreditCardTransactionReceiptId: currentReceiptId,
          plaidTransationId: creditCardTransactionId,
          isMatched: true,
          status: 'matched'
        };
        
        console.log('🔵 About to update INSTANT NOTIFICATION with:', notificationParams);
        const updateNotificationResults = await updateInstantNotification(notificationParams);
        console.log('🔵 Instant notification update results:', updateNotificationResults);
      }

    } catch (error) {
      console.error('❌ Error in matchTransactionForApproval:', error);
      // Optional: show error toast
      toast({
        status: "error",
        title: "Matching Failed",
        description: error.message || "Failed to match transaction",
        isClosable: true,
      });
    } finally {
      // Only refresh if explicitly requested (default is true for single operations)
      if (refresh) {
        setRefreshIndex(prev => prev + 1);
      }
      setIsMatching(false);
    }
  };

  const [ selectedCardholderTeamMemberId, setSelectedCardholderTeamMemberId ] = useState(null);
  const [ selectedCardholderName, setSelectedCardholderName ] = useState(null);
  const [ cardholderReceipts, setCardholderReceipts ] = useState([]);
  const [ cardholderDjts, setCardholderDjts ] = useState([]);
  const [ isLoadingCardholderData, setIsLoadingCardholderData ] = useState(false);
  const [ cardholderCounts, setCardholderCounts ] = useState({});
  // card last four → teamMemberId, kept so counts can be rebuilt on refresh
  const [ cardToTeamMember, setCardToTeamMember ] = useState({});

  // Builds the "N txn / N rcpt" counts shown in the cardholder dropdown and
  // refreshes allPendingTransactions. Runs on mount and again on every refresh.
  const buildCardholderCounts = async (cardLookup) => {
    try {
      const [pendingTxns, ...receiptResults] = await Promise.allSettled([
        fetchTransactionsByStatus('pending_match', 1000),
        ...UNMATCHED_RECEIPT_STATUSES.map(status =>
          fetchUploadedReceiptsByStatus({ status, limit: 1000 })),
      ]);

      const counts = {};
      const ensureEntry = (tmId) => {
        if (!counts[tmId]) counts[tmId] = { unmatchedTxns: 0, unmatchedReceipts: 0 };
      };

      // Transactions — resolve teamMemberId via card lookup
      const txnItems = pendingTxns.status === 'fulfilled' ? (pendingTxns.value || []) : [];
      for (const txn of txnItems) {
        if (hasLinkedReceipt(txn)) continue; // already matched
        const tmId = cardLookup[txn.accountNumber];
        if (tmId) {
          ensureEntry(tmId);
          counts[tmId].unmatchedTxns += 1;
        }
      }
      setAllPendingTransactions(txnItems);
      console.log('allPendingTransactions count:', txnItems.length);

      // Receipts — teamMemberId is on the record directly
      for (const result of receiptResults) {
        if (result.status !== 'fulfilled' || !Array.isArray(result.value)) continue;
        for (const receipt of result.value) {
          const tmId = receipt.teamMemberId;
          if (tmId) {
            ensureEntry(tmId);
            counts[tmId].unmatchedReceipts += 1;
          }
        }
      }

      // Merge so draftDjts (set in handleSelectCardholder) survives, and
      // cardholders who dropped to zero actually get zeroed out.
      setCardholderCounts(prev => {
        const next = {};
        for (const tmId of new Set([...Object.keys(prev), ...Object.keys(counts)])) {
          next[tmId] = { ...prev[tmId], unmatchedTxns: 0, unmatchedReceipts: 0, ...counts[tmId] };
        }
        return next;
      });
      console.log('cardholderCounts built:', counts);
    } catch (err) {
      console.error('Failed to build cardholder counts:', err);
    }
  };

  // Rebuild counts and pending transactions whenever handleRefresh fires
  // (after a match, detach, etc.) so the dropdown and cardholder view don't go stale.
  useEffect(() => {
    if (refreshIndex > 0 && Object.keys(cardToTeamMember).length) {
      buildCardholderCounts(cardToTeamMember);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [refreshIndex]);
  
  const handleSelectCardholder = async (option) => {
    if (!option || !option.value) {
      setSelectedCardholderTeamMemberId(null);
      setSelectedCardholderName(null);
      setCardholderReceipts([]);
      setCardholderUnmatchedTxns([]);
      setCardholderDjts([]);
      return;
    }

    const { value: teamMemberId, divisionsForSearch = [], name } = option;

    setSelectedCardholderTeamMemberId(teamMemberId);
    setSelectedCardholderName(name);
    setCardholderReceipts([]);
    setCardholderUnmatchedTxns([]);
    setCardholderDjts([]);
    setIsLoadingCardholderData(true);

    try {
      // ── Unmatched transactions ─────────────────────────────────────────
      // Filter the globally-fetched pending_match transactions to this
      // cardholder's cards. Only include rows with no receipt linked yet.
      const cards = new Set(teamMemberCardLookup[teamMemberId] || []);
      const unmatchedTxns = allPendingTransactions.filter(
        txn => cards.has(txn.accountNumber) && !hasLinkedReceipt(txn)
      );
      setCardholderUnmatchedTxns(unmatchedTxns);
      console.log('cards for this cardholder:', [...cards]);
      console.log('unmatchedTxns count:', unmatchedTxns.length);

      if (unmatchedTxns.length > 0) {
        console.log('unmatchedTxn sample:', JSON.stringify(unmatchedTxns[0], null, 2));
      }

      // ── Unmatched receipts ─────────────────────────────────────────────
      // Only fetch preemptive variants — receipts that have been uploaded
      // but not yet matched to any transaction. ready-for-approval and
      // with-notification are handled in the Ready and Auto Match tabs.
      const [
        preemptive,
        preemptiveInvoice,
        preemptiveRefund,
        preemptiveAdmin,
        preemptiveInvoiceAdmin,
        preemptiveRefundAdmin,
        withNotification,
        withNotificationAdmin,
      ] = await Promise.allSettled([
        fetchUploadedReceiptsByTeamMemberByStatus({ status: 'preemptive',               teamMemberId, limit: 100 }),
        fetchUploadedReceiptsByTeamMemberByStatus({ status: 'preemptive-invoice',       teamMemberId, limit: 100 }),
        fetchUploadedReceiptsByTeamMemberByStatus({ status: 'preemptive-refund',        teamMemberId, limit: 100 }),
        fetchUploadedReceiptsByTeamMemberByStatus({ status: 'preemptive-admin',         teamMemberId, limit: 100 }),
        fetchUploadedReceiptsByTeamMemberByStatus({ status: 'preemptive-invoice-admin', teamMemberId, limit: 100 }),
        fetchUploadedReceiptsByTeamMemberByStatus({ status: 'preemptive-refund-admin',  teamMemberId, limit: 100 }),
        fetchUploadedReceiptsByTeamMemberByStatus({ status: 'with-notification',        teamMemberId, limit: 100 }),
        fetchUploadedReceiptsByTeamMemberByStatus({ status: 'with-notification-admin',  teamMemberId, limit: 100 }),
      ]);

      const allReceipts = [
        ...(preemptive.status              === 'fulfilled' && Array.isArray(preemptive.value)              ? preemptive.value              : []),
        ...(preemptiveInvoice.status       === 'fulfilled' && Array.isArray(preemptiveInvoice.value)       ? preemptiveInvoice.value       : []),
        ...(preemptiveRefund.status        === 'fulfilled' && Array.isArray(preemptiveRefund.value)        ? preemptiveRefund.value        : []),
        ...(preemptiveAdmin.status         === 'fulfilled' && Array.isArray(preemptiveAdmin.value)         ? preemptiveAdmin.value         : []),
        ...(preemptiveInvoiceAdmin.status  === 'fulfilled' && Array.isArray(preemptiveInvoiceAdmin.value)  ? preemptiveInvoiceAdmin.value  : []),
        ...(preemptiveRefundAdmin.status   === 'fulfilled' && Array.isArray(preemptiveRefundAdmin.value)   ? preemptiveRefundAdmin.value   : []),
        ...(withNotification.status        === 'fulfilled' && Array.isArray(withNotification.value)        ? withNotification.value        : []),
        ...(withNotificationAdmin.status   === 'fulfilled' && Array.isArray(withNotificationAdmin.value)   ? withNotificationAdmin.value   : []),
      ];
      // Generate signed URLs for each receipt's uploaded documents
      const receiptsWithUrls = await Promise.all(
        allReceipts.map(async (receipt) => {
          const uploadedDocuments = receipt?.uploadedDocuments?.items || [];
          const signedUrls = await Promise.all(
            uploadedDocuments.map(async (doc) => {
              const { identityId, key, bucket } = doc?.uploadedDocument || {};
              if (identityId && key && bucket) {
                try {
                  return await getSignedUrlForFile({
                    bucketName: bucket,
                    identityId,
                    fileName: key,
                    fileSecurity: 'protected',
                  });
                } catch (err) {
                  console.error('Error fetching signed URL:', err);
                  return null;
                }
              }
              return null;
            })
          );
          return { ...receipt, signedUrls: signedUrls.filter(Boolean) };
        })
      );
      setCardholderReceipts(receiptsWithUrls);
      if (allReceipts.length > 0) {
        console.log('cardholderReceipt sample:', JSON.stringify(allReceipts[0], null, 2));
      }

      // ── Draft DJTs ─────────────────────────────────────────────────────
      console.log('divisionsForSearch for', teamMemberId, ':', divisionsForSearch);
      if (divisionsForSearch.length > 0) {
        const pendingSubmissionStatusId = await getStatusIdByStatusCode('queued-pending-submission');
        const djtResults = await searchDjtsByDivision({
          djtStatusId:    pendingSubmissionStatusId,
          divisionId:     divisionsForSearch[0],
          multiDivisions: divisionsForSearch.length > 1 ? divisionsForSearch : [],
          teamMemberId,
          djtClientId:   '',
          djtDate:       '',
          djtEndDate:    '',
          limit:         50,
          nextToken:     null,
        });
        console.log('djt search params:', {
          djtStatusId: pendingSubmissionStatusId,
          divisionId: divisionsForSearch[0],
          multiDivisions: divisionsForSearch,
          teamMemberId,
        });
        console.log('djtResults raw:', JSON.stringify(djtResults, null, 2));
        const djts = djtResults?.items || [];
        setCardholderDjts(djts);
        setCardholderCounts(prev => ({
          ...prev,
          [teamMemberId]: {
            ...prev[teamMemberId],
            draftDjts: djts.length,
          }
        }));
      } else {
        setCardholderCounts(prev => ({
          ...prev,
          [teamMemberId]: {
            ...prev[teamMemberId],
            draftDjts: 0,
          }
        }));
      }
    } catch (err) {
      console.error('handleSelectCardholder error:', err);
    } finally {
      setIsLoadingCardholderData(false);
    }
  };
  
  if (!hasEnv) {
    return (
      <Container maxW="1600" mb={12}>
        <ModuleBox>
          <Text>
            QuickBooks environment is not configured. Set the QuickBooks
            environment in the Admin dashboard.
          </Text>
        </ModuleBox>
      </Container>
    );
  }

  if (!canUseQbo && !loading && !error && hasEnv) {
    return (
      <Container maxW="1600" mb={12}>
        <ModuleBox>
          <Text>
            QuickBooks shared connection is not active. An admin may need to
            re-authorize QuickBooks from the Admin dashboard.
          </Text>
        </ModuleBox>
      </Container>
    );
  }

  return (
    <>
      <Container mb={'50px'} as="form" onSubmit={handleSubmit(updateReceipt, onError)}>
        <>
          {showInitBanner && (
            <ModuleBox mb={4}>
              <Text>
                Checking QuickBooks shared connection…
              </Text>
            </ModuleBox>
          )}

          <ReconnectCapitalOneCard Wrapper={ModuleBox} />

          {/* Search Box */}
          <ModuleBox>
            <Box>
              <Text as="span" textStyle="heading-1">Search Receipts</Text>
            </Box>
            <Box w={'40%'} mt={'10px'}>
              <Grid templateColumns="1fr auto" gap={4} alignItems="end">
                <TextInput
                  register={register}
                  errors={errors}
                  fieldname="searchInput"
                  prettyname="Search"
                  placeholder="Enter search term"
                  onChange={filterMatchedData}
                  height={'40px'}
                />

                {showSearchReset && (
                  <ButtonPrimaryPlain
                    type="button"
                    onClick={resetMatches}
                    height={'40px'}
                    name="resetMatches"
                    value="Reset"
                    px={6}
                  />
                )}
              </Grid>
            </Box>
          </ModuleBox>

          <ModuleBox>
            <Tabs 
              borderColor='var(--dark-module-divider)' 
              variant='enclosed' 
              colorScheme='spikedarktabs' 
            >
              <TabList>
                <Tab><Text>Ready ({(matchResults?.matches?.length ?? 0) + (unpublishedInvoices?.length ?? 0)})</Text></Tab>
                <Tab><Text>Verify</Text></Tab>
                <Tab><Text>Cardholders</Text></Tab>
                <Tab><Text>Prereconcile</Text></Tab>
                <Tab><Text>Chart of Accounts</Text></Tab>
                <Tab><Text>Auto Match ({autoMatchedReceipts?.length})</Text></Tab>
              </TabList>
              <TabPanels>

                {/* Ready */}
                <TabPanel>
                  <PendingAttributionNotice />
                  <TableContainer>
                    <HStack paddingBottom={'10px'}>
                      <Box w={'100%'}>
                        <Text className={searchHeaderTextClass}>Ready</Text>
                      </Box>
                      <Box>
                        <ButtonQuaternaryWithIcon
                          name='refresh'
                          leftIcon='refresh'
                          iconsize='22px'
                          isDisabled={!canUseQbo}
                          onClick={() => { handleRefresh(); }}
                          value='Refresh page'
                        />
                      </Box>
                    </HStack>
                    <Box
                      height={'600px'}
                      overflowY={isEditTransactionVendorOpen||isEditTransactionOpen ? "hidden" : "scroll"}
                      overflowX={isEditTransactionVendorOpen||isEditTransactionOpen ? "hidden" : "auto"}
                      borderRadius="6px"
                    >
                      <Table variant={'compact'} size={'compact'}>
                        <Thead>
                          <Tr>
                            <Th verticalAlign={'top'}>
                              <Text
                                as="span"
                                textStyle='dark-heading-4'
                                cursor="pointer"
                                onClick={() => onSortSubmittedReceipts('date')}
                              >Date</Text>
                            </Th>
                            <Th verticalAlign={'top'}>
                              <Text
                                as="span"
                                textStyle='dark-heading-4'
                                cursor="pointer"
                                onClick={() => onSortSubmittedReceipts('supplier')}
                              >QuickBooks Supplier</Text>
                            </Th>
                            <Th verticalAlign={'top'}>
                              <Text
                                as="span"
                                textStyle='dark-heading-4'
                                cursor="pointer"
                                onClick={() => onSortSubmittedReceipts('merchant')}
                              >Merchant Name<br />Merchant Store</Text>
                            </Th>
                            <Th verticalAlign={'top'}>
                              <Text
                                as="span"
                                textStyle='dark-heading-4'
                                cursor="pointer"
                                onClick={() => onSortSubmittedReceipts('expense-category')}
                              >Expense Category</Text>
                            </Th>
                            <Th verticalAlign={'top'}>
                              <Text
                                as="span"
                                textStyle='dark-heading-4'
                                cursor="pointer"
                                onClick={() => onSortSubmittedReceipts('amount')}
                              >Amount</Text>
                            </Th>
                            <Th verticalAlign={'top'}>
                              <Text
                                as="span"
                                textStyle='dark-heading-4'
                                cursor="pointer"
                                onClick={() => onSortSubmittedReceipts('card')}
                              >User</Text>
                            </Th>
                            <Th></Th>
                          </Tr>
                        </Thead>
                        <Tbody>
                          {matchResults?.matches?.map((match, index) => (
                            generateProcessedRow(match, index, canUseQbo)
                          ))}

                          {unpublishedInvoices?.map((invoice, index) => (
                            generateInvoiceRow(invoice, index, canUseQbo)
                          ))}
                        </Tbody>
                      </Table>
                    </Box>
                  </TableContainer>
                </TabPanel>

                {/* Verification */}
                <TabPanel>
                  <TableContainer>
                    <HStack paddingBottom={'10px'}>
                      <Box w={'100%'}>
                        <Text className={searchHeaderTextClass}>Verify</Text>
                      </Box>
                      <Box>
                        <ButtonQuaternaryWithIcon
                          name='refresh'
                          leftIcon='refresh'
                          iconsize='22px'
                          isDisabled={!canUseQbo}
                          onClick={() => { handleRefresh(); }}
                          value='Refresh page'
                        />
                      </Box>
                    </HStack>
                    <Box
                      height={'600px'}
                      overflowY={isEditTransactionVendorOpen||isEditTransactionOpen ? "hidden" : "scroll"}
                      overflowX={isEditTransactionVendorOpen||isEditTransactionOpen ? "hidden" : "auto"}
                      borderRadius="6px"
                    >
                      <Table variant={'compact'} size={'compact'}>
                        <Tbody>
                          {qbPurchases?.map((purchase) => {
                            if (purchase?.Id) {
                              const expenseId = purchase?.Id
                              const link = `https://qbo.intuit.com/app/expense?&txnId=${expenseId}`
                              return (
                                <>
                                  <Tr>
                                    <Td>{purchase?.AccountRef?.name?.split(/[:-]/)?.slice(-2)?.join(" ")}</Td>
                                    <Td>Vendor: {purchase?.EntityRef?.name}<br/>{purchase?.Line[0]?.AccountBasedExpenseLineDetail?.AccountRef?.name}<br/>Class: {purchase?.Line[0]?.AccountBasedExpenseLineDetail?.ClassRef?.name}</Td>
                                    <Td>Amount: ${purchase?.Line[0]?.Amount}</Td>
                                    <Td><a href={link} target="_blank" style={{color:'green'}}>Open QB</a></Td>
                                  </Tr>
                                </>
                              )
                            }
                          })}

                          {qbBills?.map((bill) => {
                            if (bill?.Id) {
                              const billId = bill?.Id
                              const link = `https://qbo.intuit.com/app/bill?&txnId=${billId}`
                              return (
                                <>
                                  <Tr>
                                    <Td>INVOICE</Td>
                                    <Td>Vendor: {bill?.VendorRef?.name}<br/>{bill?.Line[0]?.AccountBasedExpenseLineDetail?.AccountRef?.name}<br/>Class: {bill?.Line[0]?.AccountBasedExpenseLineDetail?.ClassRef?.name}</Td>
                                    <Td>Amount: ${bill?.Line[0]?.Amount}</Td>
                                    <Td><a href={link} target="_blank" style={{color:'green'}}>Open QB</a></Td>
                                  </Tr>
                                </>
                              )
                            }
                          })}
                        </Tbody>
                      </Table>
                    </Box>
                  </TableContainer>
                </TabPanel>

                {/* Cardholders */}
                <TabPanel>
                  <>
                    <Box>
                      <Flex alignItems="center" justifyContent="space-between" pb={'25px'}>
                        <Box>
                          <Text className={searchHeaderTextClass}>Cardholders</Text>
                        </Box>
                        <Box>
                          <ButtonQuaternaryWithIcon
                            name='refresh'
                            leftIcon='refresh'
                            iconsize='22px'
                            isDisabled={!canUseQbo}
                            onClick={() => { 
                              if (selectedCardholderTeamMemberId) {
                                handleSelectCardholder({ 
                                  value: selectedCardholderTeamMemberId, 
                                  name: selectedCardholderName,
                                  divisionsForSearch: adminUsers.find(u => u.value === selectedCardholderTeamMemberId)?.divisionsForSearch || []
                                });
                              }
                            }}
                            value='Refresh'
                          />
                        </Box>
                      </Flex>

                      <Box mt='25px' mb='25px' w={'50%'}>
                        <Text as="span" textStyle='dark-heading-3'>Select a cardholder to review:</Text>
                        <FormSelectSimple
                          register={register}
                          control={control}
                          errors={errors}
                          isRequired={false}
                          fieldName={'adminUsers'}
                          placeholder={'Start typing to search for a cardholder'}
                          optionsArray={adminUsers?.map((option, index) => {
                            const counts = cardholderCounts[option.value];
                            const txns = counts?.unmatchedTxns ?? 0;
                            const receipts = counts?.unmatchedReceipts ?? 0;
                            const total = txns + receipts;
                            const countSuffix = (txns > 0 || receipts > 0)
                              ? `  ·  ${txns} txn / ${receipts} rcpt`
                              : '';
                            return {
                              key:                index,
                              value:              option.value,
                              label:              `${option.label}${countSuffix}`,
                              divisionId:         option.divisionId,
                              name:               option.name,
                              divisionsForSearch: option.divisionsForSearch || [],
                              _total:             total,
                            };
                          }).sort((a, b) => b._total - a._total)}
                          onChange={(e) => handleSelectCardholder(e)}
                          isClearable={true}
                        />
                      </Box>

                      {isLoadingCardholderData && (
                        <Box py={6}>
                          <Center>
                            <Text>Loading cardholder data...</Text>
                          </Center>
                        </Box>
                      )}

                      {(!isLoadingCardholderData && selectedCardholderTeamMemberId) && (
                        <>
                          {/* ── Transactions Needing a Receipt ──────────────────── */}
                          <Box mt='40px' mb='10px'>
                            <Flex alignItems="center" justifyContent="space-between">
                              <Text as="span" textStyle='dark-heading-3'>
                                Transactions Needing a Receipt — {selectedCardholderName}
                              </Text>
                              <ButtonSecondaryWithIcon
                                type="button"
                                name="manualReceipt"
                                value="Add receipt"
                                onClick={onStandaloneUploadOpen}
                                leftIcon="plus"
                                iconsize="20px"
                              />
                            </Flex>
                          </Box>
                          <TableContainer>
                            <Box
                              minHeight="80px"
                              overflowY="scroll"
                              border="1px solid"
                              borderColor="var(--dark-component-border)"
                              borderRadius="6px"
                              p={2}
                            >
                              <Table size='sm'>
                                <Thead>
                                  <Tr>
                                    <Th><Text as="span" textStyle='dark-heading-4'>Date</Text></Th>
                                    <Th><Text as="span" textStyle='dark-heading-4'>Merchant</Text></Th>
                                    <Th><Text as="span" textStyle='dark-heading-4'>Amount</Text></Th>
                                    <Th><Text as="span" textStyle='dark-heading-4'>Card</Text></Th>
                                    <Th><Text as="span" textStyle='dark-heading-4'>Action</Text></Th>
                                  </Tr>
                                </Thead>
                                <Tbody>
                                  {cardholderUnmatchedTxns.length === 0 ? (
                                    <Tr>
                                      <Td colSpan={5}>
                                        <Center py={4}>
                                          <Text>No unmatched transactions found</Text>
                                        </Center>
                                      </Td>
                                    </Tr>
                                  ) : (
                                    [...cardholderUnmatchedTxns].sort((a, b) => {
                                      const dA = awsFormatUnknownDate(a.date || a.purchaseDate) || '';
                                      const dB = awsFormatUnknownDate(b.date || b.purchaseDate) || '';
                                      return dB.localeCompare(dA);
                                    }).map((txn, index) => (
                                      <Fragment key={txn.id || index}>
                                        <Tr _hover={{ bg: 'var(--table-row-hover)' }}>
                                          <Td>{spellOutDate(awsFormatUnknownDate(txn.date || txn.purchaseDate), 'apple-ny') || '—'}</Td>
                                          <Td>{txn.merchantEntityName || txn.store || '—'}</Td>
                                          <Td>{txn.amount ? formatCurrency(parseFloat(txn.amount)) : '—'}</Td>
                                          <Td>···· {txn.accountNumber}</Td>
                                          <Td>
                                            <ButtonPrimaryPlain
                                              type="button"
                                              name={`attachReceipt_${txn.id}`}
                                              value="Attach Receipt"
                                              size="sm"
                                              onClick={() => {
                                                setCurrentAttachTransaction(txn);
                                                onAttachReceiptOpen();
                                              }}
                                            />
                                          </Td>
                                        </Tr>
                                        {/* <Tr key={`divider-txn-${txn.id}`}>
                                          <Td colSpan={5} borderBottom={'1px solid var(--dark-module-divider)'}></Td>
                                        </Tr> */}
                                      </Fragment>
                                    ))
                                  )}
                                </Tbody>
                              </Table>
                            </Box>
                          </TableContainer>

                          {/* ── Uploaded Receipts Awaiting Match ────────────────── */}
                          <Box mt='40px' mb='10px'>
                            <Flex alignItems="center" justifyContent="space-between">
                              <Text as="span" textStyle='dark-heading-3'>
                                Uploaded Receipts Awaiting Match — {selectedCardholderName}
                              </Text>
                              {/* <ButtonSecondaryWithIcon
                                type="button"
                                name="manualReceipt"
                                value="Add receipt"
                                onClick={onStandaloneUploadOpen}
                                leftIcon="plus"
                                iconsize="20px"
                              /> */}
                            </Flex>
                          </Box>
                          <TableContainer>
                            <Box
                                minHeight="80px"
                                overflowY="scroll"
                                border="1px solid"
                                borderColor="var(--dark-component-border)"
                                borderRadius="6px"
                                p={2}
                              >
                              <Table size='sm'>
                                <Thead>
                                  <Tr>
                                    <Th><Text as="span" textStyle='dark-heading-4'>View</Text></Th>
                                    <Th><Text as="span" textStyle='dark-heading-4'>Date</Text></Th>
                                    <Th><Text as="span" textStyle='dark-heading-4'>Vendor</Text></Th>
                                    <Th><Text as="span" textStyle='dark-heading-4'>Amount</Text></Th>
                                    <Th><Text as="span" textStyle='dark-heading-4'>Division</Text></Th>
                                    {/* <Th><Text as="span" textStyle='dark-heading-4'>Category</Text></Th> */}
                                    {/* <Th><Text as="span" textStyle='dark-heading-4'>Notes</Text></Th> */}
                                    <Th><Text as="span" textStyle='dark-heading-4'>Type</Text></Th>
                                  </Tr>
                                </Thead>
                                <Tbody>
                                  {cardholderReceipts.length === 0 ? (
                                    <Tr>
                                      <Td colSpan={6}>
                                        <Center py={4}>
                                          <Text>No unmatched receipts found</Text>
                                        </Center>
                                      </Td>
                                    </Tr>
                                  ) : (
                                    [...cardholderReceipts].sort((a, b) => {
                                      const dA = awsFormatUnknownDate(a.invoiceReceiptDate || a.createdAt) || '';
                                      const dB = awsFormatUnknownDate(b.invoiceReceiptDate || b.createdAt) || '';
                                      return dB.localeCompare(dA);
                                    }).map((receipt, index) => {
                                      const rcptAmount = getValidAmount(
                                        receipt?.statementTotal,
                                        receipt?.amountPaid,
                                        receipt?.amountDue
                                      );
                                      const rcptDate = spellOutDate(
                                        awsFormatUnknownDate(receipt?.invoiceReceiptDate || receipt?.createdAt),
                                        'apple-ny'
                                      );
                                      // const vendor = receipt?.vendorName || '—';
                                      const vendor = receipt?.vendorName
                                        || receipt?.capitalOneInstantNotification?.purchaseMerchant
                                        || '—';
                                      const division = receipt?.division?.prettyname || '—';
                                      // const category = getExpenseCategory(receipt?.expenseCategoryId)?.label || '—';
                                      // const isAdmin = receipt.matchStatus?.endsWith('-admin');
                                      const signedUrls = receipt?.signedUrls || [];

                                      return (
                                        <Fragment key={receipt.id || index}>
                                          <Tr _hover={{ bg: 'var(--table-row-hover)' }}>
                                            <Td>
                                              {signedUrls.length > 0 ? (
                                                <IconButton
                                                  backgroundColor={'transparent'}
                                                  h="24px"
                                                  w="24px"
                                                  variant="iconOnlyTertiaryTransparent"
                                                  icon={<AvailableIcons boxSize="20px" iconRef="image"/>}
                                                  onClick={(e) => {
                                                    e.stopPropagation();
                                                    handleOpenGallery(signedUrls, {
                                                      date: rcptDate,
                                                      supplier: vendor,
                                                      amount: rcptAmount,
                                                    });
                                                  }}
                                                />
                                              ) : '—'}
                                            </Td>
                                            <Td>{rcptDate || '—'}</Td>
                                            <Td>{vendor}</Td>
                                            <Td>{rcptAmount ? `$${rcptAmount}` : '—'}</Td>
                                            <Td>{division}</Td>
                                            {/* <Td>{category}</Td>
                                            <Td style={{ wordBreak: 'break-word', whiteSpace: 'normal', maxWidth: '150px' }}>
                                              {receipt?.addedNotes || '—'}
                                            </Td> */}
                                            <Td>
                                              <Badge colorScheme={receipt.matchStatus?.includes('with-notification') ? 'green' : 'blue'} textTransform="none">
                                                {receipt.matchStatus?.includes('with-notification') ? 'Instant' : 'Manual'}
                                              </Badge>
                                            </Td>
                                          </Tr>
                                          {/* <Tr key={`divider-rcpt-${receipt.id}`}>
                                            <Td colSpan={8} borderBottom={'1px solid var(--dark-module-divider)'}></Td>
                                          </Tr> */}
                                        </Fragment>
                                      );
                                    })
                                  )}
                                </Tbody>
                              </Table>
                            </Box>
                          </TableContainer>

                          {/* ── Draft Job Tickets ────────────────────────────────── */}
                          <Box mt='40px' mb='10px'>
                            <Text as="span" textStyle='dark-heading-3'>
                              Draft Job Tickets — {selectedCardholderName}
                            </Text>
                            {cardholderDjts.length > 0 && (
                              <Text fontSize="sm" color="var(--text-secondary)" mt={1}>
                                {cardholderDjts.length} draft ticket{cardholderDjts.length !== 1 ? 's' : ''}
                              </Text>
                            )}
                          </Box>
                          {/* <Box mt='40px' mb='10px'>
                            <Text as="span" textStyle='dark-heading-3'>
                              Draft Job Tickets — {selectedCardholderName}
                            </Text>
                          </Box> */}
                          <TableContainer>
                            <Box
                              minHeight="80px"
                              overflowY="scroll"
                              border="1px solid"
                              borderColor="var(--dark-component-border)"
                              borderRadius="6px"
                              p={2}
                            >
                              <Table size='sm'>
                                <Thead>
                                  <Tr>
                                    <Th><Text as="span" textStyle='dark-heading-4'>Date</Text></Th>
                                    <Th><Text as="span" textStyle='dark-heading-4'>Client</Text></Th>
                                    <Th><Text as="span" textStyle='dark-heading-4'>Total</Text></Th>
                                    {/* <Th><Text as="span" textStyle='dark-heading-4'>Location</Text></Th> */}
                                    {/* <Th><Text as="span" textStyle='dark-heading-4'>Status</Text></Th> */}
                                    {/* <Th><Text as="span" textStyle='dark-heading-4'>Actions</Text></Th> */}
                                  </Tr>
                                </Thead>
                                <Tbody>
                                  {cardholderDjts.length === 0 ? (
                                    <Tr>
                                      <Td colSpan={3}>
                                        <Center py={4}>
                                          <Text>No draft job tickets found</Text>
                                        </Center>
                                      </Td>
                                    </Tr>
                                  ) : (
                                    [...cardholderDjts].sort((a, b) => {
                                      const dA = awsFormatUnknownDate(a.djtDate) || '';
                                      const dB = awsFormatUnknownDate(b.djtDate) || '';
                                      return dB.localeCompare(dA);
                                    }).map((djt, index) => (
                                      <Fragment key={djt.id || index}>
                                        <Tr>
                                          <Td>{spellOutDate(awsFormatUnknownDate(djt?.djtDate), 'apple-ny') || '—'}</Td>
                                          <Td>{djt?.djtClient?.contactCompany || '—'}</Td>
                                          <Td>{djt?.djtTotalBilled ? formatCurrency(parseFloat(djt.djtTotalBilled)) : '—'}</Td>
                                          {/* <Td>{djt?.djtLocation || '—'}</Td> */}
                                          {/* <Td><Badge colorScheme="yellow">{djt?.currentStatus?.statusCode || 'draft'}</Badge></Td> */}
                                          {/* <Td><ButtonPrimaryPlain type="button" name={`editDjt_${djt.id}`} value="View DJT" size="sm" onClick={() => { window.location.href = `/daily-job-ticket/${djt.id}`; }} /></Td> */}
                                        </Tr>
                                      </Fragment>
                                    ))
                                  )}
                                </Tbody>
                              </Table>
                            </Box>
                          </TableContainer>
                        </>
                      )}
                    </Box>
                  </>
                </TabPanel>

                {/* Reconciliation */}
                <TabPanel>
                  <Box>
                    <Flex alignItems="center" justifyContent="space-between" pb={'25px'}>
                      <Box>
                        <Text className={searchHeaderTextClass}>Prereconcile</Text>
                      </Box>
                    </Flex>

                    <StatementUploader 
                      onFileParsed={handleFileParsed}
                      mappedTeamMembers={mappedTeamMembers}
                      register={register}
                      control={control}
                      errors={errors}
                      statusFilter={statusFilter}
                      onStatusFilterChange={handleStatusFilterChange}
                      statusOptions={statusOptions}
                      sortField={sortField}
                      sortDirection={sortDirection}
                      onSort={handleSort}
                    />
                  </Box>
                </TabPanel>

                {/* Chart of Accounts */}
                <TabPanel>
                  <>
                    <Box>
                      <Flex alignItems="center" justifyContent="space-between" pb={'25px'}>
                        <Box>
                          <Text className={searchHeaderTextClass}>Sync Accounts</Text>
                        </Box>
                        <Spacer />
                        <Box>
                          <ButtonQuaternaryWithIcon 
                            name='closeDrawer'
                            iconsize='26px'
                            leftIcon='close'
                            value='Get QuickBooks Accounts'
                            onClick={fetchAndProcessChartOfAccounts}
                          />
                        </Box>
                      </Flex>
                      <Box>
                        <TableContainer>
                          <HStack mb='15px'>
                            <Text className={searchHeaderTextClass}>QuickBooks Accounts - Team Members</Text>
                            <Spacer/>
                          </HStack>
                          
                          <Table variant={'compact'} size={'compact'} >
                            <Tbody>
                              {/* {(dynamodbChartOfAccounts?.length===0) && <Tr><Td py='6px' colSpan={4}><Center>No results found</Center></Td></Tr>} */}
                              {(dynamodbChartOfAccounts.filter(a => a.qbChartOfAccountsActive === 'true').length === 0) && 
                                <Tr><Td py='6px' colSpan={4}><Center>No results found</Center></Td></Tr>
                              }

                              {/* {generateChartOfAccountsRows(dynamodbChartOfAccounts, mappedTeamMembers)} */}
                              {generateChartOfAccountsRows(
                                  dynamodbChartOfAccounts.filter(a => a.qbChartOfAccountsActive === 'true'),
                                  mappedTeamMembers
                                )}
                                
                              <Tr><Td borderBottom={'1px solid var(--dark-module-divider)'}></Td></Tr> 
                            </Tbody> 
                          </Table>
                        </TableContainer>
                      </Box>
                    </Box>
                  </>
                </TabPanel>

                {/* Auto Match */}
                <TabPanel>
                  <TableContainer>
                    {/* Header with Match All button */}
                    <Flex justifyContent="space-between" alignItems="center" mb={4}>
                      <Text as="span" textStyle='dark-heading-3'>
                        Auto Match ({autoMatchedReceipts?.length || 0})
                      </Text>
                      {autoMatchedReceipts?.length > 0 && (
                        <ButtonPrimaryPlain 
                          type="button"
                          name="matchAllTransactions"
                          value={
                            isMatchingAll
                              ? `Matching... (${autoMatchedReceipts.length})`
                              : `Match All (${autoMatchedReceipts.length})`
                          }
                          isDisabled={isMatchingAll || isMatching}
                          onClick={matchAllTransactions}
                        />
                      )}
                    </Flex>

                    <Box
                      height={'600px'}
                      overflowY="scroll"
                      border="1px solid"
                      borderColor="var(--dark-component-border)"
                      borderRadius="6px"
                      p={2}
                      onScroll={handleScroll}
                    >
                      <Table variant={'compact'} size={'compact'} maxW={'100%'} w={'100%'}>
                        <Thead>
                          <Tr>
                            <Th></Th>
                            <Th><Text as="span" textStyle='dark-heading-4'>Date</Text></Th>
                            <Th><Text as="span" textStyle='dark-heading-4'>Merchant</Text></Th>
                            <Th><Text as="span" textStyle='dark-heading-4'>Notes</Text></Th>
                            <Th><Text as="span" textStyle='dark-heading-4'>Amount</Text></Th>
                            <Th><Text as="span" textStyle='dark-heading-4'>Card</Text></Th>
                            <Th>Match</Th>
                          </Tr>
                        </Thead>
                        <Tbody>
                          {!autoMatchedReceipts || autoMatchedReceipts.length === 0 ? (
                            <Tr>
                              <Td colSpan={7}>
                                <Center py={8}>
                                  <Text>No auto-matched receipts found</Text>
                                </Center>
                              </Td>
                            </Tr>
                          ) : (
                            autoMatchedReceipts?.map((matched, index) => {
                              const signedUrls = matched.signedUrls||[];
                              return (
                                <Fragment key={matched.id || index}>
                                  <Tr>
                                    <Td>
                                      {(signedUrls?.length > 0) ? (
                                        <IconButton
                                          backgroundColor={'transparent'}
                                          h="24px"
                                          w="24px"
                                          variant="iconOnlyTertiaryTransparent"
                                          icon={<AvailableIcons boxSize="20px" iconRef="image"/>}
                                          onClick={(e) => {
                                            e.stopPropagation();
                                            handleOpenGallery(signedUrls, {
                                              date: matched?.matchedTransaction?.date, 
                                              supplier: matched?.matchedTransaction?.store, 
                                              amount: matched?.matchedTransaction?.amount
                                            });
                                          }}
                                        />
                                      ) : null }
                                    </Td>
                                    <Td>
                                      Rcpt: {awsFormatUnknownDate(matched?.invoiceReceiptDate)}<br/>
                                      Txn: {matched?.matchedTransaction?.date}
                                    </Td>
                                    <Td>
                                      {matched?.vendorName}<br/>
                                      {matched?.matchedTransaction?.store}
                                    </Td>
                                    <Td style={{ wordBreak: 'break-word', whiteSpace: 'normal' }}>
                                      {matched?.addedNotes}
                                    </Td>
                                    <Td>
                                      ${matched?.capitalOneInstantNotification?.purchaseAmount || matched?.statementTotal || matched?.amountPaid || matched?.amountDue || ''}<br/>
                                      ${matched?.matchedTransaction?.amount}
                                    </Td>
                                    <Td>
                                      {matched?.cardLastFour}<br/>
                                      {matched?.matchedTransaction?.accountNumber}
                                    </Td>
                                    <Td>
                                      <Box paddingTop={'10px'} paddingBottom={'10px'}>
                                        <ButtonPrimaryPlain 
                                          type="button"
                                          name='matchTransaction'
                                          value={isMatching ? 'Matching' : 'Match'}
                                          isDisabled={isMatching || isMatchingAll}
                                          onClick={() => matchTransactionForApproval(matched)}
                                        />
                                      </Box>
                                    </Td>
                                  </Tr>
                                  {/* <Tr>
                                    <Td colSpan={7} borderBottom={'1px solid var(--dark-module-divider)'}></Td>
                                  </Tr> */}
                                </Fragment>
                              );
                            })
                          )}
                        </Tbody>
                      </Table>
                    </Box>
                  </TableContainer>
                </TabPanel>


              </TabPanels>
            </Tabs>
          </ModuleBox>

          {(userIsDeveloper && lastQbError) && (
            <>
              <Box
                mt={6}
                p={4}
                borderWidth="1px"
                borderRadius="md"
                bg="gray.800"
                _light={{ bg: "var(--table-row-hover)" }}
              >
                <HStack justify="space-between" mb={2}>
                  <Text fontWeight="bold" fontSize="sm">
                    QuickBooks Publish Debug
                  </Text>
                  <Button
                    size="xs"
                    variant="ghost"
                    onClick={() => setLastQbError(null)}
                  >
                    Clear
                  </Button>
                </HStack>

                <Text fontSize="xs" mb={2}>
                  <strong>Stage:</strong> {lastQbError.stage || "n/a"}{" "}
                  <strong>ErrorType:</strong> {lastQbError.errorType || "n/a"}
                </Text>

                {lastQbError.attachments && (
                  <Text fontSize="xs" mb={2}>
                    <strong>Attachments:</strong>{" "}
                    {lastQbError.attachments.attached || 0} attached,{" "}
                    {lastQbError.attachments.failed || 0} failed of{" "}
                    {lastQbError.attachments.total || 0}
                  </Text>
                )}

                <Box
                  as="pre"
                  fontSize="xs"
                  whiteSpace="pre-wrap"
                  maxH="250px"
                  overflowY="auto"
                  p={2}
                  borderWidth="1px"
                  borderRadius="md"
                >
                  {JSON.stringify(lastQbError, null, 2)}
                </Box>
              </Box>
            </>
          )}
        </>
      </Container>

      <ModuleDrawer
        onClose={onEditTransactionClose}
        isOpen={isEditTransactionOpen}
        bodyContent={
          <EditTransaction 
            handleMapVendor={mapQBVendorToStatementVendor} 
            transaction={currentEditTransaction} 
            onClose={onEditTransactionClose} 
            environment={qbEnv}
            expenseCategories={expenseCategories}
            selectedExpenseCategory={selectedExpenseCategoryOption}
            qbVendors={qbVendors}
            register={register}
            control={control}
            errors={errors}
            setError={setError}
            clearErrors={clearErrors}
            setValue={setValue}
            getValues={getValues}
            updateExpenseCategory={updateSelectedExpenseCategory}
            rowIndex={currentEditTransactionIndex}
            updateReceipt={handlePublish}
            handleSubmit={handleSubmit}
            adminName={adminName}
            isPublishing={isPublishingToQuickBooks}
            handleDetachReceipt={handleDetachReceipt}
            onDocumentsFetched={setUploadedDocuments}
          />}
        size={'full'}
      />

      <ModuleDrawer
        onClose={onEditTransactionVendorClose}
        isOpen={isEditTransactionVendorOpen}
        bodyContent={<EditTransactionVendor 
            transaction={currentEditTransactionVendor} 
            onClose={onEditTransactionVendorClose} 
            environment={qbEnv}
            qbVendors={qbVendors}
            mapVendor={mapQBVendorToStatementVendor}
            register={register}
            control={control}
            errors={errors}
            setError={setError}
            clearErrors={clearErrors}
            setValue={setValue}
            rowIndex={currentEditTransactionVendorIndex}/>
        }
        size={'full'}
      />

      {/* Attach Receipt to Transaction — admin uploads file for a specific txn */}
      <ModuleDrawer
        onClose={() => {
          onAttachReceiptClose();
          setAttachReceiptMode('upload');
        }}
        isOpen={isAttachReceiptOpen}
        bodyContent={
          <>  
            {/* Mode toggle */}
            {/* <Flex mb={4} gap={2}>
              <ButtonPrimaryPlain
                type="button"
                name="modeUpload"
                value="Upload File"
                size="sm"
                variant={attachReceiptMode === 'upload' ? 'solid' : 'outline'}
                onClick={() => setAttachReceiptMode('upload')}
              />
              <ButtonPrimaryPlain
                type="button"
                name="modeLinkExisting"
                value="Use Existing Receipt"
                size="sm"
                variant={attachReceiptMode === 'link-existing' ? 'solid' : 'outline'}
                onClick={() => setAttachReceiptMode('link-existing')}
              />
            </Flex> */}
            <Tabs
              borderColor='var(--dark-module-divider)'
              variant='enclosed'
              colorScheme='spikedarktabs'
              index={attachReceiptMode === 'upload' ? 0 : 1}
              onChange={(i) => setAttachReceiptMode(i === 0 ? 'upload' : 'link-existing')}
              mb={4}
            >
              <TabList>
                <Tab><Text>Upload File</Text></Tab>
                <Tab><Text>Use Existing Receipt</Text></Tab>
              </TabList>
              <TabPanels>
                <TabPanel px={0}>
                  <QuickCameraUploadStatement
                    key={currentAttachTransaction?.id}
                    id={currentAttachTransaction?.id}
                    selectedActionType="upload-with-transaction"
                    forceFileMode={true}
                    targetTeamMemberId={selectedCardholderTeamMemberId}
                    attachReceiptParams={currentAttachTransaction ? {
                      id:            currentAttachTransaction.id,
                      amount:        currentAttachTransaction.amount,
                      date:          currentAttachTransaction.date,
                      name:          currentAttachTransaction.merchantEntityName || currentAttachTransaction.store || '',
                      accountNumber: currentAttachTransaction.accountNumber,
                    } : null}
                    onClose={() => {
                      onAttachReceiptClose();
                      setAttachReceiptMode('upload');
                      if (selectedCardholderTeamMemberId) {
                        handleSelectCardholder({
                          value: selectedCardholderTeamMemberId,
                          name: selectedCardholderName,
                          divisionsForSearch: adminUsers.find(u => u.value === selectedCardholderTeamMemberId)?.divisionsForSearch || [],
                        });
                      }
                    }}
                  />
                </TabPanel>

                <TabPanel px={0}>
                  <Box>
                    <Flex alignItems="center" justifyContent="space-between" mb={4}>
                      <Text as="span" textStyle='dark-heading-3'>
                        Select a receipt to link to this transaction
                      </Text>
                      <ResponsiveCancelCloseButton
                        iconSize='26px'
                        isMobile={false}
                        value="Cancel"
                        isDisabled={isLinkingReceipt}
                        onClick={() => {
                          setPendingLinkReceipt(null);
                          onAttachReceiptClose();
                          setAttachReceiptMode('upload');
                        }}
                      />
                    </Flex>

                    {currentAttachTransaction && (
                      <Box mb={4} p={3} borderRadius="6px" border="1px solid" borderColor="var(--dark-component-border)">
                        <Text fontSize="sm" color="var(--text-secondary)">Linking to transaction:</Text>
                        <Text fontWeight="semibold">
                          {currentAttachTransaction.merchantEntityName || currentAttachTransaction.store || '—'}
                          {' · '}
                          {spellOutDate(awsFormatUnknownDate(currentAttachTransaction.date), 'apple-ny')}
                          {' · '}
                          {currentAttachTransaction.amount ? formatCurrency(parseFloat(currentAttachTransaction.amount)) : '—'}
                        </Text>
                      </Box>
                    )}

                    {/* Confirmation block — appears after a receipt is selected */}
                    {pendingLinkReceipt && (
                      <Box mb={4} p={3} borderRadius="6px" border="1px solid" borderColor="var(--success-green)" bg="var(--success-green-subtle)">
                        <Text fontSize="sm" fontWeight="semibold" mb={1}>Confirm link:</Text>
                        <Text fontSize="sm">
                          Receipt from <strong>{pendingLinkReceipt.vendorName || '—'}</strong>
                          {' · '}
                          {spellOutDate(awsFormatUnknownDate(pendingLinkReceipt.invoiceReceiptDate || pendingLinkReceipt.createdAt), 'apple-ny')}
                          {' · '}
                          {getValidAmount(pendingLinkReceipt?.statementTotal, pendingLinkReceipt?.amountPaid, pendingLinkReceipt?.amountDue)
                            ? `$${getValidAmount(pendingLinkReceipt.statementTotal, pendingLinkReceipt.amountPaid, pendingLinkReceipt.amountDue)}`
                            : '—'}
                        </Text>
                        <Flex mt={3} gap={2}>
                          <ButtonPrimaryPlain
                            type="button"
                            name="confirmLink"
                            value="Confirm Link"
                            size="sm"
                            isLoading={isLinkingReceipt}
                            onClick={handleLinkExistingReceipt}
                          />
                          <ButtonSecondaryPlain
                            type="button"
                            name="cancelLink"
                            value="Cancel"
                            size="sm"
                            isDisabled={isLinkingReceipt}
                            onClick={() => setPendingLinkReceipt(null)}
                          />
                        </Flex>
                      </Box>
                    )}

                    {cardholderReceipts.length === 0 ? (
                      <Text>No unmatched receipts available to link.</Text>
                    ) : (
                      <Table size='sm'>
                        <Thead>
                          <Tr>
                            <Th><Text as="span" textStyle='dark-heading-4'>Date</Text></Th>
                            <Th><Text as="span" textStyle='dark-heading-4'>Vendor</Text></Th>
                            <Th><Text as="span" textStyle='dark-heading-4'>Amount</Text></Th>
                            <Th><Text as="span" textStyle='dark-heading-4'>Type</Text></Th>
                            <Th></Th>
                          </Tr>
                        </Thead>
                        <Tbody>
                          {[...cardholderReceipts].sort((a, b) => {
                            const dA = awsFormatUnknownDate(a.invoiceReceiptDate || a.createdAt) || '';
                            const dB = awsFormatUnknownDate(b.invoiceReceiptDate || b.createdAt) || '';
                            return dB.localeCompare(dA);
                          }).map((receipt, index) => {
                            const rcptAmount = getValidAmount(receipt?.statementTotal, receipt?.amountPaid, receipt?.amountDue);
                            const rcptDate = spellOutDate(awsFormatUnknownDate(receipt?.invoiceReceiptDate || receipt?.createdAt), 'apple-ny');
                            const isPending = pendingLinkReceipt?.id === receipt.id;
                            return (
                              <Tr
                                key={receipt.id || index}
                                _hover={{ bg: 'var(--table-row-hover)' }}
                                bg={isPending ? 'var(--success-green-subtle)' : undefined}
                              >
                                <Td>{rcptDate || '—'}</Td>
                                <Td>{receipt?.vendorName || '—'}</Td>
                                <Td>{rcptAmount ? `$${rcptAmount}` : '—'}</Td>
                                <Td>
                                  <Badge colorScheme={receipt.matchStatus?.includes('with-notification') ? 'green' : 'blue'} textTransform="none">
                                    {receipt.matchStatus?.includes('with-notification') ? 'Instant' : 'Manual'}
                                  </Badge>
                                </Td>
                                <Td>
                                  <ButtonPrimaryPlain
                                    type="button"
                                    name={`linkReceipt_${receipt.id}`}
                                    value={isPending ? 'Selected' : 'Select'}
                                    size="sm"
                                    isDisabled={isLinkingReceipt}
                                    onClick={() => setPendingLinkReceipt(isPending ? null : receipt)}
                                  />
                                </Td>
                              </Tr>
                            );
                          })}
                        </Tbody>
                      </Table>
                    )}

                  </Box>
                </TabPanel>
            </TabPanels>
          </Tabs>
          </>
        }
        size={'full'}
      />

      {/* Standalone Upload — admin uploads a receipt with no transaction yet */}
      <ModuleDrawer
        onClose={onStandaloneUploadClose}
        isOpen={isStandaloneUploadOpen}
        bodyContent={
          <QuickCameraUploadStatement
            showCancel={true}
            selectedActionType="preemptive"
            forceFileMode={true}
            targetTeamMemberId={selectedCardholderTeamMemberId}
            onClose={() => {
              onStandaloneUploadClose();
              if (selectedCardholderTeamMemberId) {
                handleSelectCardholder({
                  value: selectedCardholderTeamMemberId,
                  name: selectedCardholderName,
                  divisionsForSearch: adminUsers.find(u => u.value === selectedCardholderTeamMemberId)?.divisionsForSearch || [],
                });
              }
            }}
          />
        }
        size={'full'}
      />

      {(galleryImages?.length>0) && <ReceiptImageViewerModal
        isModalOpen={isReceiptImageViewerModalOpen}
        onModalClose={handleReceiptImageViewerModalClose}
        images={galleryImages}
        transaction={galleryTransaction}
      />}
    </>
  )
};